import { Injectable } from '@angular/core'
import {
  BehaviorSubject,
  concat,
  EMPTY,
  forkJoin,
  from,
  Observable,
  of,
  race,
  Subject,
  Subscription,
  timer,
} from 'rxjs'
import { catchError, finalize, map, switchMap, take, takeWhile, tap, timeout } from 'rxjs/operators'
import { visibilityAwareTimer } from 'src/utils/visibility-aware-timer'
import { PageVisibilityService } from 'src/app/services/page-visibility.service'
import { CrudAction, CrudEvent, CrudSubject } from 'src/utils/crud-subject'
import { Arn, MatchingRuleInfo, SelectableResource, Tag } from '../../../utils/types'
import { ResourceType } from 'src/utils/resource-types'
import { ApiService } from '../../services/api.service'
import {
  adaptApplicationIssueDto,
  AllRuleDto,
  ApplicationDto,
  ApplicationIntegrationDto,
  ApplicationIssueDto,
  ApplicationIssueScopeDto,
  ApplicationResourceDto,
  ApplicationResourcesResponseDto,
  ApplicationRestoreRequestDto,
  ApplicationType,
  AppResourceStatusChangeDto,
  AppResourceStatusChangesResponseDto,
  ArnRuleDto,
  AwsSourceResourceDto,
  AwsTargetResourceDto,
  BaseRuleDto,
  ExcludeWeakArnRuleDto,
  FailoverTestResultDto,
  RecoveryPointAttemptDto,
  RecoveryPointDto,
  ResourceConfigDto,
  ResourceScope,
  ResourceGroupRuleDto,
  ResourceTypeRuleDto,
  RestoreRequestDto,
  RestoreTriggerDto,
  RestoreTypeDto,
  SyncPhaseDto,
  TagRuleDto,
} from '../../services/dtos'
import { SyncPair, SyncPairProviderService } from './sync-pair.service'
import { AccountService } from './account.service'
import { SyncSource, SyncSourceProviderService } from './sync-source.service'
import { ApplicationResource } from 'src/app/shared/resource-viewer-base'
import {
  areDeeplyEqual,
  dateDiff,
  diffSets,
  isLaterThan,
  mapToObject,
  maxDate,
  maxOrAnyDate,
} from 'src/app/shared/utils'
import { adaptApplicationIssueDtoToRaisedIssue, DeleteFailedIssue, RaisedIssue, RaisedIssueScope } from './issue'
import { DedupableIssue, dedupeIssues } from './issue-dedup'
import { ResourceStatus } from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { computeProcessStatus, ProcessStatus } from 'src/app/account/components/process-state/process-state.component'
import { CrossTabSyncService } from 'src/app/services/cross-tab-sync.service'

/** An issue plus the DTO it came from, so a section can be split by DTO scope after deduping. */
interface IssueSectionRow extends DedupableIssue {
  dto: ApplicationIssueDto
  raised: RaisedIssue
}

/** Deduped issues grouped the way the Issues page renders them. */
interface IssueSections {
  recoveryPoint: RaisedIssue[]
  scan: RaisedIssue[]
  backup: RaisedIssue[]
  restore: RaisedIssue[]
  failback: RaisedIssue[]
  all: RaisedIssue[]
}

/** Shared, not per-call: template bindings read the issue getters on every change
 *  detection pass, so a fresh object each time would allocate for nothing. */
const NO_ISSUE_SECTIONS: IssueSections = {
  recoveryPoint: [],
  scan: [],
  backup: [],
  restore: [],
  failback: [],
  all: [],
}

export interface BaseRule {}

export class ArnRule implements BaseRule {
  static ruleType = 'arn'
  arnSet: Array<Arn>
  operator: string

  constructor(dto: ArnRuleDto) {
    this.arnSet = dto.arns
    this.operator = dto.operator || 'is'
  }
}

export class ExcludeWeakArnRule implements BaseRule {
  static ruleType = 'excludeWeakArn'
  arnSet: Array<Arn>
  operator: string

  constructor(dto: ExcludeWeakArnRuleDto) {
    this.arnSet = dto.arns
    this.operator = dto.operator || 'is'
  }
}

export class TagRule implements BaseRule {
  static ruleType = 'tag'
  tagName: string
  tagValue: string | null
  operator: string

  constructor(dto: TagRuleDto) {
    this.tagName = dto.name
    this.tagValue = dto.value
    this.operator = dto.operator || 'is'
  }
}

export class ResourceTypeRule implements BaseRule {
  static ruleType = 'resourceType'
  resourceType: string
  operator: string

  constructor(dto: ResourceTypeRuleDto) {
    this.resourceType = dto.resourceType
    this.operator = dto.operator || 'is'
  }
}

export class ResourceGroupRule implements BaseRule {
  static ruleType = 'resourceGroup'
  resourceGroup: string
  operator: string

  constructor(dto: ResourceGroupRuleDto) {
    this.resourceGroup = dto.resourceGroup
    this.operator = dto.operator || 'is'
  }
}

export class AllRule implements BaseRule {
  static ruleType = 'all'
  rules: BaseRule[]

  constructor(dto: AllRuleDto) {
    this.rules = dto.rules.map(ruleDtoToRule)
  }
}

function ruleDtoToRule(dto: BaseRuleDto) {
  switch (dto.ruleType) {
    case ArnRule.ruleType:
      return new ArnRule(dto as ArnRuleDto)
    case ExcludeWeakArnRule.ruleType:
      return new ExcludeWeakArnRule(dto as ExcludeWeakArnRuleDto)
    case AllRule.ruleType:
      return new AllRule(dto as AllRuleDto)
    case TagRule.ruleType:
      return new TagRule(dto as TagRuleDto)
    case ResourceTypeRule.ruleType:
      return new ResourceTypeRule(dto as ResourceTypeRuleDto)
    case ResourceGroupRule.ruleType:
      return new ResourceGroupRule(dto as ResourceGroupRuleDto)
  }
  throw `Unknown rule type: ${dto.ruleType}`
}

function createTagRuleDto(tag: Tag, action?: string): TagRuleDto {
  const dto: TagRuleDto = {
    ruleType: TagRule.ruleType,
    name: tag.tagName,
    value: tag.tagValue ?? null,
    operator: (tag as any).operator ?? 'is',
    action: action === 'exclude' ? 'exclude' : null,
  }
  return dto
}

function createResourceTypeRuleDto(resourceTypeRule: ResourceTypeRule, action?: string): ResourceTypeRuleDto {
  const dto: ResourceTypeRuleDto = {
    ruleType: ResourceTypeRule.ruleType,
    resourceType: resourceTypeRule.resourceType,
    operator: resourceTypeRule.operator || 'is',
    action: action === 'exclude' ? 'exclude' : null,
  }
  return dto
}

function createResourceGroupRuleDto(resourceGroupRule: ResourceGroupRule, action?: string): ResourceGroupRuleDto {
  const dto: ResourceGroupRuleDto = {
    ruleType: ResourceGroupRule.ruleType,
    resourceGroup: resourceGroupRule.resourceGroup,
    operator: resourceGroupRule.operator || 'is',
    action: action === 'exclude' ? 'exclude' : null,
  }
  return dto
}

function createAllRuleDto(allRule: AllRule, action?: string): AllRuleDto {
  const dto: AllRuleDto = {
    ruleType: AllRule.ruleType,
    rules: allRule.rules.map((rule) => {
      if (rule instanceof TagRule) {
        return createTagRuleDto(rule, action)
      } else if (rule instanceof ArnRule) {
        return createArnRuleDto(new Set(rule.arnSet))
      } else if (rule instanceof ExcludeWeakArnRule) {
        return createExcludeWeakArnRuleDto(new Set(rule.arnSet))
      } else if (rule instanceof ResourceTypeRule) {
        return createResourceTypeRuleDto(rule, action)
      } else if (rule instanceof ResourceGroupRule) {
        return createResourceGroupRuleDto(rule, action)
      } else {
        throw new Error(`Unknown rule type: ${rule.constructor.name}`)
      }
    }),
    action: action === 'exclude' ? 'exclude' : null,
  }

  return dto
}

function createArnRuleDto(arns: Set<Arn>): ArnRuleDto {
  return {
    ruleType: ArnRule.ruleType,
    arns: Array.from(arns),
    operator: 'is',
  }
}

function createExcludeWeakArnRuleDto(arns: Set<Arn>): ExcludeWeakArnRuleDto {
  return {
    ruleType: ExcludeWeakArnRule.ruleType,
    arns: Array.from(arns),
    operator: 'is',
  }
}

export interface RestoreRequest {
  application: Application
  recoveryPointId?: string
  isRealTime?: boolean
  networkSandboxEnabled?: boolean
  advancedNetworkSandbox?: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedDomains?: string[]
  networkSandboxAllowedServices?: string[]
  quarantineEnabled?: boolean
  malwareScanEnabled?: boolean
  forceNewTargetPlan?: boolean
  protectRecoveryPoint?: boolean
}

function adaptRestoreRequest(req: RestoreRequest, syncPhase: SyncPhaseDto): [string, ApplicationRestoreRequestDto] {
  let dto = {
    phase: syncPhase,
  } as ApplicationRestoreRequestDto

  if (!!req.recoveryPointId) {
    dto.recoveryPointId = req.recoveryPointId
  }
  if (req.isRealTime !== undefined) {
    dto.enableRealTimeSync = req.isRealTime
  }
  if (req.networkSandboxEnabled !== undefined) {
    dto.enableNetworkSandbox = req.networkSandboxEnabled
  }
  if (dto.enableNetworkSandbox && req.networkSandboxWhitelist !== undefined) {
    dto.networkSandboxWhitelist = req.networkSandboxWhitelist
  }
  if (dto.enableNetworkSandbox && req.networkSandboxAllowedDomains !== undefined) {
    dto.networkSandboxAllowedDomains = req.networkSandboxAllowedDomains
  }
  if (dto.enableNetworkSandbox && req.networkSandboxAllowedServices !== undefined) {
    dto.networkSandboxAllowedServices = req.networkSandboxAllowedServices
  }
  if (req.advancedNetworkSandbox !== undefined) {
    dto.advancedNetworkSandbox = req.advancedNetworkSandbox
  }
  if (req.quarantineEnabled !== undefined) {
    dto.enableQuarantine = req.quarantineEnabled
  }
  if (req.malwareScanEnabled !== undefined) {
    dto.enableMalwareScan = req.malwareScanEnabled
  }
  if (req.forceNewTargetPlan !== undefined) {
    dto.forceNewTargetPlan = req.forceNewTargetPlan
  }
  if (req.protectRecoveryPoint) {
    // can only be set to true during restore
    dto.protectRecoveryPoint = req.protectRecoveryPoint
  }
  return [req.application.applicationId, dto]
}

export class Application {
  static BACKUP_POLL_INTERVAL = 15000
  static RESTORE_POLL_INTERVAL = 2000
  static BACKGROUND_POLL_INTERVAL = 60000 // 1 minute (standby)
  // How long the issue revisions are trusted before refetching regardless. A revision can be
  // lost — concurrent cross-region increments converge under last-writer-wins, and a failed bump
  // is swallowed server-side — so this bounds how long the issue counts can stay wrong.
  static ISSUE_FORCE_REFRESH_INTERVAL = 600000 // 10 minutes
  // Backstop for a restore whose request never shows up in a GET at all. Sized for cross-region
  // read lag, which self-corrects in about a second, not for the backend's own step-function
  // crash window: once a GET carries lastRequest, the DTO drives the masking without the latch.
  static LOCAL_RESTORE_LATCH_TTL = 30 * 1000

  private static _matchesRuleSet(
    resource: SelectableResource,
    rules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    setMatchingRules: boolean = false
  ): Map<string, MatchingRuleInfo> {
    /**
     * Helper function to check if a resource matches any rule in a set of rules.
     * Top-level rules use OR logic (any rule can match).
     * AllRule inner rules use AND logic (all inner rules must match).
     * Returns a Map of matching rules (empty if no matches).
     */
    const matchingRulesMap = new Map<string, MatchingRuleInfo>()

    // OR logic: iterate through each top-level rule
    for (const topLevelRule of rules) {
      if ('rules' in topLevelRule) {
        // Handle AllRule (AND logic for inner rules)
        let allRule = topLevelRule as AllRule

        // Separate tag rules, resource type rules, and resource group rules
        const tagRules = allRule.rules.filter((rule) => rule instanceof TagRule) as TagRule[]
        const resourceTypeRules = allRule.rules.filter((rule) => rule instanceof ResourceTypeRule) as ResourceTypeRule[]
        const resourceGroupRules = allRule.rules.filter(
          (rule) => rule instanceof ResourceGroupRule
        ) as ResourceGroupRule[]

        // For AllRule: ALL inner rules must match (AND logic)
        const matchingTagRules = new Map<string, MatchingRuleInfo>()
        const allTagRulesMatch =
          tagRules.length === 0 ||
          tagRules.every((rule) => {
            const tagMatches =
              (rule.tagValue === null && resource.tags.has(rule.tagName)) ||
              resource.tags.get(rule.tagName) === rule.tagValue
            const ruleResult = rule.operator === 'is_not' ? !tagMatches : tagMatches
            if (ruleResult) {
              const matchingRuleInfo = { value: rule.tagValue, operator: rule.operator || 'is' }
              matchingTagRules.set(rule.tagName, matchingRuleInfo)
            }
            return ruleResult
          })

        const matchingResourceTypeRules = new Map<string, MatchingRuleInfo>()
        const allResourceTypeRulesMatch =
          resourceTypeRules.length === 0 ||
          resourceTypeRules.every((rule) => {
            const resourceTypeMatches = resource.resourceType === rule.resourceType
            const ruleResult = rule.operator === 'is_not' ? !resourceTypeMatches : resourceTypeMatches
            if (ruleResult) {
              const key = `__resourceType:${rule.resourceType}`
              const matchingRuleInfo = { value: rule.resourceType, operator: rule.operator || 'is' }
              matchingResourceTypeRules.set(key, matchingRuleInfo)
            }
            return ruleResult
          })

        const matchingResourceGroupRules = new Map<string, MatchingRuleInfo>()
        const allResourceGroupRulesMatch =
          resourceGroupRules.length === 0 ||
          resourceGroupRules.every((rule) => {
            // For Azure Resource Group resources, use the resource name directly
            let resourceGroupValue = resource.resourceGroup
            if (resource.resourceType === ResourceType.azureResourceGroup) {
              resourceGroupValue = resource.resourceName
            }

            // Case-insensitive comparison for Azure resource groups
            const resourceGroupMatches = resourceGroupValue?.toLowerCase() === rule.resourceGroup?.toLowerCase()
            const ruleResult = rule.operator === 'is_not' ? !resourceGroupMatches : resourceGroupMatches
            if (ruleResult) {
              const key = `__resourceGroup:${rule.resourceGroup}`
              const matchingRuleInfo = { value: rule.resourceGroup, operator: rule.operator || 'is' }
              matchingResourceGroupRules.set(key, matchingRuleInfo)
            }
            return ruleResult
          })

        // The AllRule matches if ALL tag rules match AND ALL resource type rules match AND ALL resource group rules match
        if (allTagRulesMatch && allResourceTypeRulesMatch && allResourceGroupRulesMatch) {
          // Add all matching rules from this AllRule to the result
          if (setMatchingRules) {
            matchingTagRules.forEach((value, key) => {
              resource.matchingRules.set(key, value)
            })
            matchingResourceTypeRules.forEach((value, key) => {
              resource.matchingRules.set(key, value)
            })
            matchingResourceGroupRules.forEach((value, key) => {
              resource.matchingRules.set(key, value)
            })
          }

          matchingTagRules.forEach((value, key) => {
            matchingRulesMap.set(key, value)
          })
          matchingResourceTypeRules.forEach((value, key) => {
            matchingRulesMap.set(key, value)
          })
          matchingResourceGroupRules.forEach((value, key) => {
            matchingRulesMap.set(key, value)
          })

          // Since this is OR logic at the top level, if any rule matches, we can continue to check other rules
          // but we don't return early since we want to collect all matching rules
        }
      } else if (topLevelRule instanceof ResourceGroupRule) {
        // Handle top-level ResourceGroupRule
        // For Azure Resource Group resources, use the resource name directly
        let resourceGroupValue = resource.resourceGroup
        if (resource.resourceType === ResourceType.azureResourceGroup) {
          resourceGroupValue = resource.resourceName
        }

        // Case-insensitive comparison for Azure resource groups
        const resourceGroupMatches = resourceGroupValue?.toLowerCase() === topLevelRule.resourceGroup?.toLowerCase()
        const matchResult = topLevelRule.operator === 'is_not' ? !resourceGroupMatches : resourceGroupMatches
        if (matchResult) {
          const key = `__resourceGroup:${topLevelRule.resourceGroup}`
          const matchingRuleInfo = { value: topLevelRule.resourceGroup, operator: topLevelRule.operator || 'is' }
          if (setMatchingRules) {
            resource.matchingRules.set(key, matchingRuleInfo)
          }
          matchingRulesMap.set(key, matchingRuleInfo)
        }
      } else if (topLevelRule instanceof ResourceTypeRule) {
        // Handle top-level ResourceTypeRule
        const resourceTypeMatches = resource.resourceType === topLevelRule.resourceType
        const matchResult = topLevelRule.operator === 'is_not' ? !resourceTypeMatches : resourceTypeMatches
        if (matchResult) {
          const key = `__resourceType:${topLevelRule.resourceType}`
          const matchingRuleInfo = { value: topLevelRule.resourceType, operator: topLevelRule.operator || 'is' }
          if (setMatchingRules) {
            resource.matchingRules.set(key, matchingRuleInfo)
          }
          matchingRulesMap.set(key, matchingRuleInfo)
        }
      } else {
        // Handle top-level Tag
        let { tagName, tagValue, operator } = topLevelRule as Tag
        const tagMatches = (tagValue === null && resource.tags.has(tagName)) || resource.tags.get(tagName) === tagValue
        const ruleResult = operator === 'is_not' ? !tagMatches : tagMatches
        if (ruleResult) {
          const matchingRuleInfo = { value: tagValue, operator: operator || 'is' }
          if (setMatchingRules) {
            resource.matchingRules.set(tagName, matchingRuleInfo)
          }
          matchingRulesMap.set(tagName, matchingRuleInfo)
        }
      }
    }

    return matchingRulesMap
  }

  private static _updateResourceRuleSelection(
    matchingArns: Set<Arn>,
    suppressedArns: Set<Arn>,
    matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    applicationResources: Array<SelectableResource>
  ) {
    /**
     * Does an in-place update of the ApplicationResource fields related to selection rules.
     */

    let transitiveMatchesToProcess: Array<string> = new Array<string>()
    let excludedResourceArns = new Set<string>()

    // First, identify all excluded resources
    applicationResources.forEach((resource) => {
      if (resource.resourceArn && resource.tags) {
        const excludeRuleResults = Application._matchesRuleSet(resource, excludingRules, false)
        if (excludeRuleResults.size > 0) {
          excludedResourceArns.add(resource.resourceArn)
        }
      }
    })

    applicationResources.forEach((resource) => {
      let matchesRule = false

      if (resource.resourceArn && resource.tags) {
        // Look to see if this ID matches one that should be selected.
        if (matchingArns.has(resource.resourceArn)) {
          resource.arnMatch = true
          matchesRule = true
        }

        // Also check on tag matches using the extracted helper function
        const matchingRuleResults = Application._matchesRuleSet(resource, matchingRules, true)
        if (matchingRuleResults.size > 0) {
          matchesRule = true
        }

        if (matchesRule) {
          transitiveMatchesToProcess.push(resource.resourceArn)
        }
      }
    })

    // Now that we've marked the resources that match rules explicitly, discover the
    // transitive matches and mark them as well.
    let processedMatches = new Set<string>()
    let kvMapArray = applicationResources.map((res) => [res.resourceArn, res] as [string, SelectableResource])
    let appResourceMap = new Map(kvMapArray)

    const parentChildMap = new Map<string, SelectableResource[]>()
    applicationResources.forEach((resource) => {
      if (resource.parentArn) {
        let children = parentChildMap.get(resource.parentArn)
        if (!children) {
          children = []
          parentChildMap.set(resource.parentArn, children)
        }
        children.push(resource)
      }
    })

    function _getDescendants(parentArn: string) {
      const descendants = []
      const children = parentChildMap.get(parentArn)
      if (children) {
        children.forEach((child: SelectableResource) => {
          descendants.push(child)
          descendants.push(..._getDescendants(child.resourceArn))
        })
      }
      return descendants
    }

    // Add direct dependees and descendents of rule matches.  These are only applied to direct rule matches (not
    // transitively).
    let directDependeeArns: string[] = []
    for (let dependerArn of transitiveMatchesToProcess) {
      // Skip if this resource is excluded
      if (excludedResourceArns.has(dependerArn)) {
        continue
      }

      let depender = appResourceMap.get(dependerArn)
      if (depender) {
        for (let dependeeArn of depender.directDependees) {
          let dependee = appResourceMap.get(dependeeArn)
          if (dependee && !excludedResourceArns.has(dependeeArn)) {
            directDependeeArns.push(dependeeArn)
            dependee.directDependeeOf.add(dependerArn)
          }
        }

        const descendants = _getDescendants(dependerArn)
        descendants.forEach((descendant: SelectableResource) => {
          if (!excludedResourceArns.has(descendant.resourceArn)) {
            directDependeeArns.push(descendant.resourceArn)
          }
        })
      }
    }

    while (transitiveMatchesToProcess.length > 0 || directDependeeArns.length > 0) {
      let dependentResourceArn = null
      if (transitiveMatchesToProcess.length > 0) {
        dependentResourceArn = transitiveMatchesToProcess.pop()
      } else {
        dependentResourceArn = directDependeeArns.pop()
      }

      // Skip if this resource is excluded
      if (excludedResourceArns.has(dependentResourceArn)) {
        continue
      }

      let dependentResource = appResourceMap.get(dependentResourceArn)
      dependentResource.dependees.forEach((dependeeArn: string) => {
        // Skip if the dependee is excluded
        if (excludedResourceArns.has(dependeeArn)) {
          return
        }

        // add this resource to dependeeOf on each dependee resource.
        let dependeeResource = appResourceMap.get(dependeeArn)
        if (dependeeResource) {
          // add the dependentResourceArn to the dependeeOf list on the dependee resource.
          dependeeResource.dependeeOf.add(dependentResourceArn)
          // If we haven't already processed this dependentResource, we need to
          // queue it up for processing.
          if (!processedMatches.has(dependeeArn)) {
            transitiveMatchesToProcess.push(dependeeArn)
          }
        }
      })

      if (dependentResource.weakDependees.length > 0) {
        // Do the same dependee calculation for weak dependees, but only if they aren't being excluded
        dependentResource.weakDependees.forEach((weakDependeeArn: string) => {
          // Skip if the weak dependee is excluded
          if (excludedResourceArns.has(weakDependeeArn)) {
            return
          }

          // Only add to weakDependeeOf if the resource is not excluded
          let weakDependeeResource = appResourceMap.get(weakDependeeArn)
          if (weakDependeeResource) {
            if (!suppressedArns.has(weakDependeeArn)) {
              weakDependeeResource.weakDependeeOf.add(dependentResourceArn)
              // If we haven't already processed this dependentResource, we need to
              // queue it up for processing.
              if (!processedMatches.has(weakDependeeArn)) {
                transitiveMatchesToProcess.push(weakDependeeArn)
              }
            } else {
              weakDependeeResource.suppressedWeakDependeeOf.add(dependentResourceArn)
            }
          }
        })
      }
      // collect this dependentResourceArn in our list of already processed dependent resources
      processedMatches.add(dependentResourceArn)
    }

    // Process exclude rules AFTER all transitive processing is complete
    // Exclude rules take precedence and override any previous selections
    applicationResources.forEach((resource) => {
      if (resource.resourceArn && resource.tags) {
        // Use the extracted helper function for exclude rule matching
        const excludeRuleResults = Application._matchesRuleSet(resource, excludingRules, false)

        // If resource matches exclude rule, clear all selections
        if (excludeRuleResults.size > 0) {
          // Clear any matching tags set by include rules
          resource.matchingRules.clear()
          resource.arnMatch = false
          // Clear all dependency relationships that would cause this resource to be selected
          resource.dependeeOf.clear()
          resource.weakDependeeOf.clear()
          resource.directDependeeOf.clear()
          // Mark as excluded for UI purposes
          resource.isExcluded = true
        }
      }
    })
  }

  static updateApplicationResourceRuleSelection(
    applications: Array<Application>,
    applicationResources: Array<SelectableResource>
  ) {
    /**
     * Process resource rules for a set of Application objects.
     */

    // initialize the selection rule accounting fields.
    applicationResources.forEach((resource) => {
      resource.arnMatch = false
      resource.matchingRules = new Map<string, MatchingRuleInfo>()
      resource.dependeeOf = new Set<string>()
      resource.weakDependeeOf = new Set<string>()
      resource.suppressedWeakDependeeOf = new Set<string>()
      resource.directDependeeOf = new Set<string>()
      resource.isExcluded = false
    })

    for (let application of applications) {
      Application._updateResourceRuleSelection(
        application.matchingArns,
        application.suppressedArns,
        application.matchingRules,
        application.excludingRules,
        applicationResources
      )
    }
  }

  static updateResourceRuleSelection(
    matchingArns: Set<Arn>,
    excludedWeakArns: Set<Arn>,
    matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    applicationResources: Array<SelectableResource>
  ) {
    /**
     * Apply rules outside of the context of an Application.  This is used in experiences
     * where we are creating a new application and don't yet have an Application object.
     */

    // initialize the selection rule accounting fields.
    applicationResources.forEach((resource) => {
      resource.arnMatch = false
      resource.matchingRules = new Map<string, MatchingRuleInfo>()
      resource.dependeeOf = new Set<string>()
      resource.weakDependeeOf = new Set<string>()
      resource.suppressedWeakDependeeOf = new Set<string>()
      resource.directDependeeOf = new Set<string>()
      resource.isExcluded = false
    })

    Application._updateResourceRuleSelection(
      matchingArns,
      excludedWeakArns,
      matchingRules,
      excludingRules,
      applicationResources
    )
  }

  // pub-sub for application updates as they happen.
  static publishSubject$ = new CrudSubject<Application>()

  // Cached most recent failover test result, whatever its outcome.
  // undefined = not yet queried (show "---"); null = queried, no test found (show "Never")
  private _latestFailoverTestResult: FailoverTestResultDto = undefined
  private _latestFailoverTestQueryServerTimestamp: Date = null

  // Optimistic sync phase override — set synchronously when a restore is initiated
  // (conclude, launch, retry, etc.) so the UI reflects the target phase in the brief
  // window between the click and the first DTO refresh. Cleared implicitly by the
  // syncPhase getter once the DTO catches up (either dto.syncPhase or
  // dto.restoreStatus.lastRequestSyncPhase matches). See ARP-6718 for the full logic.
  private _optimisticSyncPhaseOverride: SyncPhaseDto = null

  // Latched restore-request time, held from the moment a restore is initiated. The backend has
  // written last_restore_request by the time the POST returns, but a GET can be served by a region
  // whose replica hasn't caught up yet, and that read drops currentlyRestoring and collapses
  // restoreHorizon — unmasking the previous restore's SUCCEEDED statuses as if the new one had
  // already finished. Set from the client clock at request time, then replaced with the backend's
  // own timestamp once the POST response carries it (see confirmRestoreRequestLatch).
  private _localRestoreRequestAt: Date = null

  // The DTO's restore start as it read when the latch was set. The latch releases by comparing this
  // against the current DTO — server timestamp to server timestamp, the way _dtoReflectsOptimistic
  // does — so the release never depends on the client clock agreeing with the backend's.
  private _localRestoreRequestBaselineStart: Date = null

  // Wall-clock deadline (ms epoch) backstopping the latch if the backend never registers the
  // request at all. Client clock on both sides of the comparison (see
  // _optimisticReconciliationDeadline), so skew can't strand it.
  private _localRestoreRequestDeadline: number = null

  // cached network sandboxing override
  private _networkSandboxOverride: boolean = null
  private _quarantineOverride: boolean = null

  // Per-scope resource caches — null means "not yet fetched", empty array means "fetched but no resources"
  private _backupResources: ApplicationResourceDto[] = null
  private _restoreResources: ApplicationResourceDto[] = null
  private _failbackResources: ApplicationResourceDto[] = null
  // Persistent ARN→DTO lookup merged from every scope's most recent fetch. Survives scope
  // invalidation so the Issues tab can still enrich issues (resourceType, resourceName) that
  // belong to a scope that isn't currently active. Without this, `makeViewableIssues()` strips
  // metadata from any issue whose scope's array has been nulled.
  private _resourceDtoLookup: Map<string, ApplicationResourceDto> = new Map()
  private _backupStatuses: Map<string, AppResourceStatusChangeDto> = new Map()
  private _restoreStatuses: Map<string, AppResourceStatusChangeDto> = new Map()
  private _failbackStatuses: Map<string, AppResourceStatusChangeDto> = new Map()
  private _backupStatusNextToken: string = null
  private _restoreStatusNextToken: string = null
  private _failbackStatusNextToken: string = null

  private _rulesFromServer: BaseRuleDto[] = null

  // used to save the applied RP ID for the app when we last refreshed the list of resources
  // when the RP changes the list of resources we need to display may also change and we need
  // to refresh it
  private _lastResourceRefreshRp: string = null

  // sync source and sync pair
  private _syncSource: SyncSource = null
  private _syncPair: SyncPair = null
  private _syncPairLastQueryServerTimestamp: Date = null

  // Recovery point attempt DTOs (multi-account only)
  private _recoveryPointAttempts: RecoveryPointAttemptDto[] = null
  private _recoveryPointAttemptsServerTimestamp: Date = null
  private _recoveryPoints: Array<RecoveryPointDto> = null
  private _recoveryPointsServerTimestamp: Date = null

  // Application Issues DTOs (multi-account only)
  private _applicationIssueDtos: Array<ApplicationIssueDto> = null
  // The latest backup/restore/failback completion the cached issues were fetched against.
  private _lastSeenProcessingCompletion: Date = null
  // The issue revisions the cached issues were fetched against. Null until the first fetch.
  private _lastSeenIssueRevision: number = null
  private _lastSeenSourceIssueRevision: number = null
  // Wall-clock time of the last issue request. Null until the first fetch.
  private _issuesFetchedAt: number = null
  private _extendedIssueRefreshSub: Subscription = null // tracks the post-job-completion issue polling timer
  private _issueSections: IssueSections = null

  private _forcePoll$ = new Subject<void>()
  forceImmediatePoll(): void {
    this._forcePoll$.next()
  }

  applyOptimisticSyncPhase(phase: SyncPhaseDto): void {
    // overwrite any stale local guess
    this._optimisticSyncPhaseOverride = phase
    // immediate sidebar update with the right phase
    Application.publishSubject$.upsert(this, this.applicationId)
  }

  // Integrations associted with this application:
  private _integrationDtos: Array<ApplicationIntegrationDto> = []

  private _appResourceCache: Map<ResourceScope, [Date, Array<ApplicationResource>]> = null

  // A timestamp that's updated by the foreground refresh loop after resources and statuses are updated.  Used for
  // invalidation of entries in the _appResourceCache
  private _refreshTimestamp = new Date()

  // Track previous processing states to detect job completion transitions
  private _wasBackingUp = false
  private _wasRestoring = false
  private _wasFailingBack = false

  // Latched once we observe the first restore attempt of the current FAILOVER_TEST
  // cycle finish (success or failure). Stays true through subsequent restores in
  // the same cycle and resets when syncPhase leaves FAILOVER_TEST or a new test
  // cycle begins. Drives the cancel-test vs conclude-test UI so it doesn't
  // flicker as currentlyRestoring toggles between attempts (ARP-6708).
  private _firstRestoreCompletedInTest = false

  // Previous-tick value of restoreStatus.lastRequestSyncPhase, used to detect a
  // new FAILOVER_TEST cycle starting (prior phase ≠ FAILOVER_TEST → current =
  // FAILOVER_TEST). On that boundary we clear the latch so the new cycle starts
  // fresh, even if syncPhase never visibly transitioned through STANDBY (rapid
  // conclude → relaunch).
  private _prevLastRequestSyncPhase: SyncPhaseDto | null = null

  // Active scopes for conditional resource loading — only active scopes are fetched from the API
  private _activeScopes = new Set<ResourceScope>(['backup'])

  // Set by setOptimisticBackupResources() — prevents loadApplication$ from clearing the
  // optimistic data via invalidateResourceCaches(). Cleared after the first real API refresh.
  hasOptimisticResources = false

  // Wall-clock deadline (ms epoch) for the post-save reconciliation window. While in this
  // window we poll fast so the optimistic rows flip to real data and currentlyBackingUp can
  // light up without sitting at the 60s background cadence. Outlives hasOptimisticResources
  // because that flag is cleared on the first non-empty response, but the backend may still
  // not have registered the backup request yet.
  private _optimisticReconciliationDeadline: number | null = null

  constructor(
    private applicationDto: ApplicationDto,
    private apiService: ApiService,
    private accountService: AccountService,
    private syncSourceProviderService: SyncSourceProviderService,
    private syncPairProviderService: SyncPairProviderService
  ) {
    this.applicationDto = applicationDto
    this._appResourceCache = new Map()
  }

  static create(
    applicationDto: ApplicationDto,
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService
  ) {
    /*
    Create an application object from a DTO and publish it for subscribers to get notified
    */
    let newApp = new Application(applicationDto, apiService, accountService, ssProviderService, spProviderService)
    Application.publishSubject$.upsert(newApp, newApp.applicationId)
    return newApp
  }

  static createApplication$(
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService,
    accountId: string,
    applicationType: ApplicationType,
    applicationName: string,
    applicationRpo: number,
    applicationNotificationEmails: Array<string>,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    selectionRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    selectionArns: Set<Arn>,
    excludeWeakArns: Set<Arn>,
    identityProviderId: string = null
  ) {
    /*
      Invoke the server API to create a new application.
    */

    // Build selectionRules array, supporting Tag, AllRule, ResourceTypeRule, and ResourceGroupRule
    let ruleDtos = selectionRules
      .map((rule) => {
        // Check if the tag/rule has an action property indicating it's an exclude rule
        const action = (rule as any).action

        if ('rules' in rule) {
          return createAllRuleDto(rule as AllRule, action)
        } else if (rule instanceof ResourceTypeRule) {
          return createResourceTypeRuleDto(rule, action)
        } else if (rule instanceof ResourceGroupRule) {
          return createResourceGroupRuleDto(rule, action)
        } else {
          return createTagRuleDto(rule as Tag, action)
        }
      })
      .filter((rule) => {
        if (rule.ruleType === 'all') {
          ;(rule as AllRuleDto).rules = Application.prototype.filterEmptyNameRules((rule as AllRuleDto).rules)
          return (rule as AllRuleDto).rules.length > 0
        }
        return true
      })

    let arnRule = null
    if (selectionArns.size > 0) {
      arnRule = createArnRuleDto(selectionArns)
    }
    let excludeWeakArnRule = null
    if (excludeWeakArns.size > 0) {
      excludeWeakArnRule = createExcludeWeakArnRuleDto(excludeWeakArns)
    }

    return apiService
      .createApplication(
        accountId,
        applicationType,
        applicationName,
        applicationRpo,
        applicationNotificationEmails,
        sourceEndpointUri,
        targetEndpointUri,
        ruleDtos,
        arnRule,
        excludeWeakArnRule,
        identityProviderId
      )
      .pipe(
        map((appDto) => Application.create(appDto, apiService, accountService, ssProviderService, spProviderService))
      )
  }

  static loadAllApplications$(
    accountId: string,
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService
  ) {
    /*
      Load all applications from the server.  This is invoked on initial load of the client to populate the application cache and notify the navigation pane
      of the applications that should be displayed.
    */
    return apiService.listApplications(accountId).pipe(
      map((appDtos) => {
        appDtos.map((appDto) =>
          Application.create(appDto, apiService, accountService, ssProviderService, spProviderService)
        )
      })
    )
  }

  static loadApplication$(
    accountId: string,
    applicationId: string,
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService,
    forceRefreshResources: boolean = false
  ) {
    /*
      Load 1 application from the server.  This is called from the component for an application via the ApplicationProviderService.
    */
    return apiService.getApplication(accountId, applicationId).pipe(
      map((appDto) => Application.create(appDto, apiService, accountService, ssProviderService, spProviderService)),
      switchMap((app) => app.refresh$(false, true, forceRefreshResources))
    )
  }

  static sameSyncPair(app1: Application, app2: Application) {
    return (
      app1.sourceAwsAccountId === app2.sourceAwsAccountId &&
      app1.sourceRegion === app2.sourceRegion &&
      app1.targetAwsAccountId === app2.targetAwsAccountId &&
      app1.targetRegion === app2.targetRegion
    )
  }

  // The backend's automatic-restore lock is scoped to the region pair (account + source/target
  // region), independent of the AWS accounts. Loaded apps are already within one account, so a
  // region-only match mirrors that lock scope — broader than sameSyncPair, which also keys on
  // the AWS account IDs.
  static sameRegionPair(app1: Application, app2: Application) {
    return app1.sourceRegion === app2.sourceRegion && app1.targetRegion === app2.targetRegion
  }

  private setSyncPair(syncPair: SyncPair) {
    this._syncPair = syncPair
    this._syncPairLastQueryServerTimestamp = this.maxProcessingCompletionDate()
  }

  initiateBackup$() {
    /*
      Initiates a backup of just this application and poll until it completes.
      Emits twice: once immediately with the API response DTO (so the component can update
      the status bar right away), then again after the polling delay with refreshed data.
    */
    return this.apiService.initiateBackup(this.accountId, [this.applicationId]).pipe(
      tap((backupResponseDto) => {
        let thisAppDto = backupResponseDto.apps.find((appDto) => appDto.appId === this.applicationId)
        if (thisAppDto) {
          this.applicationDto = thisAppDto
        }
      }),
      // Emit the current application immediately (with updated DTO from the API response),
      // then continue with delayed polling. Without this, the component wouldn't see the
      // updated lastBackupRequest timestamp for 15s.
      switchMap(() =>
        concat(
          of(this as Application),
          timer(Application.BACKUP_POLL_INTERVAL).pipe(switchMap(() => this.refresh$(true)))
        )
      )
    )
  }

  initiateRestore$(
    useLatestRecoveryPoint: boolean = false,
    forceNewTargetPlan: boolean = false,
    restoreType?: RestoreTypeDto
  ) {
    /*
      Initiate a restore of just this application and poll until it completes.
      An omitted restoreType is left out of the request body entirely.
    */
    // Latch the request time up front so the masking holds even if a poll lands before
    // the backend's last_restore_request write does.
    this.latchRestoreRequest()

    let restoreRequests = new Map<string, ApplicationRestoreRequestDto>([
      [this.applicationId, {} as ApplicationRestoreRequestDto],
    ])

    if (forceNewTargetPlan) {
      restoreRequests.get(this.applicationId).forceNewTargetPlan = forceNewTargetPlan
    }

    let buildRestoreRequests$ = of(restoreRequests)

    if (useLatestRecoveryPoint) {
      buildRestoreRequests$ = this.apiService.listApplicationRecoveryPoints(this.accountId, this.applicationId).pipe(
        map((recoveryPoints) => {
          const latestRecoveryPoint = recoveryPoints.reduce((latest, current) => {
            return current.timestamp > (latest?.timestamp || 0) ? current : latest
          }, null)

          if (latestRecoveryPoint) {
            restoreRequests.get(this.applicationId).recoveryPointId = latestRecoveryPoint.recoveryPointId
          }
          return restoreRequests
        })
      )
    }

    // Pipe the restore request to the API, emit immediately with the updated DTO,
    // then continue with delayed polling so the component can show status right away.
    return buildRestoreRequests$.pipe(
      switchMap((restoreRequests) => {
        const restoreRequestDto: RestoreRequestDto = { apps: mapToObject(restoreRequests) }
        if (restoreType) {
          restoreRequestDto.restoreType = restoreType
        }
        return this.apiService.initiateRestore(this.accountId, restoreRequestDto)
      }),
      tap((restoreResponseDto) => {
        const thisAppDto = restoreResponseDto.apps.find((appDto) => appDto.appId === this.applicationId)
        if (thisAppDto) {
          this.applicationDto = thisAppDto
        }
        this.confirmRestoreRequestLatch(thisAppDto?.restoreStatus?.lastRequest)
      }),
      switchMap(() =>
        concat(
          of(this as Application),
          timer(Application.RESTORE_POLL_INTERVAL).pipe(switchMap(() => this.refresh$(true)))
        )
      ),
      catchError((err) => {
        this.clearRestoreRequestLatch()
        throw err
      })
    )
  }

  initiateRestoreFromJob$() {
    /*
      Initiate a new restore based on the application's last restore Job.
      Emits immediately with updated DTO, then continues with delayed polling.
    */
    const restoreRequestDto: RestoreRequestDto = {
      retryJobId: this.applicationDto.restoreStatus.lastJobId,
      restoreType: RestoreTypeDto.RESUME,
    }

    this.latchRestoreRequest()

    return this.apiService.initiateRestore(this.accountId, restoreRequestDto).pipe(
      tap((restoreResponseDto) => {
        const thisAppDto = restoreResponseDto.apps.find((appDto) => appDto.appId === this.applicationId)
        if (thisAppDto) {
          this.applicationDto = thisAppDto
        }
        this.confirmRestoreRequestLatch(thisAppDto?.restoreStatus?.lastRequest)
      }),
      switchMap(() =>
        concat(
          of(this as Application),
          timer(Application.RESTORE_POLL_INTERVAL).pipe(switchMap(() => this.refresh$(true)))
        )
      ),
      catchError((err) => {
        this.clearRestoreRequestLatch()
        throw err
      })
    )
  }

  /** Cancel extended issue refresh timer. Called when the application is abandoned
   *  (e.g., user navigates to a different app) to prevent stale API calls. */
  cancelExtendedIssueRefresh(): void {
    if (this._extendedIssueRefreshSub) {
      this._extendedIssueRefreshSub.unsubscribe()
      this._extendedIssueRefreshSub = null
    }
  }

  /* This method refreshes every 5 seconds for 240 seconds (wall-clock).
   * Uses visibilityAwareTimer so hidden tabs poll at 60s instead of 5s. */
  private _startExtendedIssueRefresh() {
    // Cancel any existing extended refresh to prevent overlapping timers
    if (this._extendedIssueRefreshSub) {
      this._extendedIssueRefreshSub.unsubscribe()
    }

    // Periodic refresh of issues for 240 seconds after job completion
    const refreshInterval = 5000 // 5 seconds
    const refreshDuration = 240000 // 240 seconds
    const startTime = Date.now()

    // BehaviorSubject drives the polling loop (same pattern as backgroundRefresh$).
    // Starts with 0 for an immediate first poll; subsequent polls use refreshInterval.
    const pollIntervals = new BehaviorSubject<number>(0)

    this._extendedIssueRefreshSub = pollIntervals
      .pipe(
        switchMap((interval) => visibilityAwareTimer(interval)),
        // Wall-clock cutoff — the 240s window is not extended by visibility throttling
        takeWhile(() => Date.now() - startTime < refreshDuration),
        switchMap(() =>
          this._fetchIssues$(this.accountId, this.applicationId).pipe(
            tap((issueDtos) => {
              this.setIssues(issueDtos)
              Application.publishSubject$.upsert(this, this.applicationId)
              pollIntervals.next(refreshInterval)
            }),
            catchError((err) => {
              // Don't let a transient API error kill the polling loop — schedule next poll
              console.warn('Extended issue refresh error, scheduling retry:', err)
              pollIntervals.next(refreshInterval)
              return EMPTY as Observable<Array<any>>
            })
          )
        ),
        finalize(() => {
          this._extendedIssueRefreshSub = null
        })
      )
      .subscribe()
  }

  /** Fetch this application's issues, stamping the time the server was asked.
   *
   *  Only a real request advances the stamp, so a poll that reuses the cache can't hold the
   *  periodic forced refresh open. */
  private _fetchIssues$(accountId: string, appId: string): Observable<Array<ApplicationIssueDto>> {
    return this.apiService.listApplicationIssues(accountId, appId).pipe(tap(() => (this._issuesFetchedAt = Date.now())))
  }

  /** Whether the cached issue DTOs might be out of date, and so need refetching.
   *
   *  Driven by the server's issue revisions, which change whenever this application's issues (or
   *  its sync source's, which are reported against every application sharing that source) are
   *  created, cleared, or deleted.
   *
   *  Revisions are compared for inequality only — they are not ordered across regions. */
  private _issuesMayBeStale(appDto: ApplicationDto): boolean {
    // Nothing cached to compare against, so fetch only if the server has ever recorded an
    // issue for this application or its sync source.
    if (this._applicationIssueDtos === null) {
      return !!appDto.issueRevision || !!appDto.sourceIssueRevision
    }

    // A job finished since the last fetch. Its final issues land around the time it completes,
    // so take one guaranteed look rather than waiting for a revision to arrive.
    if (this.maxProcessingCompletionDate() > this._lastSeenProcessingCompletion) return true

    // Periodic hard refresh, in case a revision bump was lost.
    if (
      this._issuesFetchedAt === null ||
      Date.now() - this._issuesFetchedAt > Application.ISSUE_FORCE_REFRESH_INTERVAL
    ) {
      return true
    }

    return (
      appDto.issueRevision !== this._lastSeenIssueRevision ||
      appDto.sourceIssueRevision !== this._lastSeenSourceIssueRevision
    )
  }

  /** Apply a DTO from the account-wide list poll, refreshing issues when they may have changed.
   *
   *  Issues are fetched per application because only the per-application endpoint exposes them. */
  public applyBackgroundDto$(appDto: ApplicationDto): Observable<Application> {
    this.applicationDto = appDto

    // refresh issues if needed
    let issues$ = of(this._applicationIssueDtos)
    if (this._issuesMayBeStale(appDto)) {
      issues$ = this._fetchIssues$(appDto.accountId, appDto.appId)
    }

    return issues$.pipe(
      map((issues) => {
        this.setIssues(issues)

        // Re-publish to components that are monitoring applications
        Application.publishSubject$.upsert(this, this.applicationId)

        return this as Application
      }),
      catchError((err) => {
        console.warn('Background issue refresh error for ' + this.applicationId + ':', err)
        // Publish anyway so a failed issue fetch costs only the issue counts, not the DTO update.
        Application.publishSubject$.upsert(this, this.applicationId)
        return of(this as Application)
      })
    )
  }

  private _applyStatusChanges(
    statuses: Map<string, AppResourceStatusChangeDto>,
    changes: AppResourceStatusChangeDto[]
  ) {
    // Apply status changes from the server.  They are returned in order so we apply them in order where the last
    // change for a given arn wins.
    for (const change of changes) {
      if (change.state) {
        statuses.set(change.arn, change)
      } else {
        statuses.delete(change.arn)
      }
    }
  }

  private _buildRefreshObservable$(refreshDto: boolean, forceRefreshResources: boolean = false) {
    // This method implements an rxjs version of the _buildRefreshObservable$ method in the SyncPair
    // and SyncSource classes.  I thought that might make it easier to read, but it's still a bitch.

    // This subject will emit the polling interval values.  We want to poll immediately the first time, so
    // we construct with a zero value.  Subsequent poll intervals are provided to the subject based upon
    // the state of the application and whether it's currently backing up or restoring.
    let pollIntervals = new BehaviorSubject<number>(0)
    let firstPoll = true

    return pollIntervals.pipe(
      // throttled when tab is hidden
      switchMap((pollInterval) => race(visibilityAwareTimer(pollInterval), this._forcePoll$.pipe(map(() => 0 as 0)))),
      switchMap((_) => {
        // build an observable that will refresh the application, including all of the additional
        // fields on the application that come along with multi-account
        let appDto$ = of(this.applicationDto)
        if (refreshDto) {
          // 30s timeout prevents indefinite hangs on network stalls — the catchError
          // below will schedule a retry so polling resumes automatically
          appDto$ = this.apiService.getApplication(this.accountId, this.applicationId).pipe(timeout(30000))
        }

        // after the first pass, we always want to refresh the appDto
        refreshDto = true

        return appDto$.pipe(
          switchMap((appDto) => {
            // build an observable that will kick off parallel updates of the various fields that are maintained
            // on the application object.  None of these observables should poll, so this will complete relatively quickly.
            const previousSyncPhase = this.syncPhase
            this.applicationDto = appDto

            // Did the effective syncPhase move on since the cached restore resources were fetched?
            // (e.g. STANDBY → FAILOVER_TEST on a test start, or FAILOVER_TEST → STANDBY on conclude.)
            const restorePhaseChanged = previousSyncPhase !== this.syncPhase
            // If so, the leftover status-next-token belongs to the PRIOR phase's restore. Drop it (and
            // its stale deltas) so we don't stream stale incremental updates onto the new phase.
            if (restorePhaseChanged) {
              this._restoreStatusNextToken = null
              this._restoreStatuses = new Map()
            }

            const oldRules = this._rulesFromServer
            const newRules = appDto.selectionRules
            const rpChanged = this._lastResourceRefreshRp !== appDto.recoveryPointId
            const rulesChanged = !areDeeplyEqual(oldRules, newRules)

            // Detect job completion transitions - when a job just finished, force a resource refresh
            // to ensure we get the final status for all resources
            const backupJustCompleted = this._wasBackingUp && !this.currentlyBackingUp
            const restoreJustCompleted = this._wasRestoring && !this.currentlyRestoring
            const failbackJustCompleted = this._wasFailingBack && !this.currentlyFailingBack
            const jobJustCompleted = backupJustCompleted || restoreJustCompleted || failbackJustCompleted

            // Latch the first-restore-complete flag for the current FAILOVER_TEST cycle.
            // Reset on exit from FAILOVER_TEST; also reset on a new test cycle boundary
            // (prior request phase ≠ FAILOVER_TEST flipping to FAILOVER_TEST), so a rapid
            // conclude → relaunch doesn't carry the prior cycle's latch into the new one.
            // Then latch on either an observed true→false restore transition, or a DTO
            // that already shows the most recent FAILOVER_TEST-scoped request has completed
            // (covers page reload mid-test). The lastRestoreRequest truthy guard defends
            // against the JS quirk where `null >= null` is true via Number coercion.
            //
            // Known limitation: page reload while a *subsequent* restore is in flight will
            // show CANCEL TEST until that restore completes — the DTO can't tell whether a
            // pre-existing lastSuccess belongs to this cycle's prior restore or a previous
            // (concluded) cycle, and we don't persist the latch across reloads.
            const currentLastRequestSyncPhase = this.applicationDto.restoreStatus?.lastRequestSyncPhase ?? null
            const newTestCycleStarted =
              this._prevLastRequestSyncPhase !== null &&
              this._prevLastRequestSyncPhase !== SyncPhaseDto.FAILOVER_TEST &&
              currentLastRequestSyncPhase === SyncPhaseDto.FAILOVER_TEST

            if (this.syncPhase !== SyncPhaseDto.FAILOVER_TEST) {
              this._firstRestoreCompletedInTest = false
            } else {
              if (newTestCycleStarted) {
                this._firstRestoreCompletedInTest = false
              }
              if (this._wasRestoring && !this.currentlyRestoring) {
                this._firstRestoreCompletedInTest = true
              } else if (
                currentLastRequestSyncPhase === SyncPhaseDto.FAILOVER_TEST &&
                this.lastRestoreRequest &&
                this.lastRestoreComplete &&
                this.lastRestoreComplete >= this.lastRestoreRequest
              ) {
                this._firstRestoreCompletedInTest = true
              }
            }

            this._prevLastRequestSyncPhase = currentLastRequestSyncPhase

            // Update previous processing state tracking
            this._wasBackingUp = this.currentlyBackingUp
            this._wasRestoring = this.currentlyRestoring
            this._wasFailingBack = this.currentlyFailingBack

            this._rulesFromServer = newRules

            // refresh app resources if needed — only for active scopes
            let backupResources$ = of({
              resources: this._backupResources,
              statusNextToken: this._backupStatusNextToken,
            } as ApplicationResourcesResponseDto)
            let restoreResources$ = of({
              resources: this._restoreResources,
              statusNextToken: this._restoreStatusNextToken,
            } as ApplicationResourcesResponseDto)
            let failbackResources$ = of({
              resources: this._failbackResources,
              statusNextToken: this._failbackStatusNextToken,
            } as ApplicationResourcesResponseDto)

            const globalRefreshNeeded =
              rulesChanged ||
              rpChanged ||
              forceRefreshResources ||
              jobJustCompleted ||
              this.lastBackupComplete > this._recoveryPointAttemptsServerTimestamp

            // When global conditions change, invalidate inactive scopes so they refresh when activated
            if (globalRefreshNeeded) {
              if (!this._activeScopes.has('backup')) this._backupResources = null
              if (!this._activeScopes.has('restore')) this._restoreResources = null
              if (!this._activeScopes.has('failback')) this._failbackResources = null
            }

            // Refresh a scope if it's active AND (global change, never fetched, or needs a status token for incremental polling)
            const shouldRefreshBackup =
              this._activeScopes.has('backup') &&
              (globalRefreshNeeded ||
                this._backupResources === null ||
                (this.currentlyBackingUp && !this._backupStatusNextToken))
            const shouldRefreshRestore =
              this._activeScopes.has('restore') &&
              (globalRefreshNeeded ||
                this._restoreResources === null ||
                // Force an immediate reload on phase change (start/conclude).
                // Prevents a one-cycle delay in UI updates when a restore ends.
                restorePhaseChanged ||
                (this.currentlyRestoring && !this._restoreStatusNextToken))
            const shouldRefreshFailback =
              this._activeScopes.has('failback') &&
              (globalRefreshNeeded ||
                this._failbackResources === null ||
                (this.currentlyFailingBack && !this._failbackStatusNextToken))

            if (shouldRefreshBackup || shouldRefreshRestore || shouldRefreshFailback) {
              this._lastResourceRefreshRp = appDto.recoveryPointId
            }
            if (shouldRefreshBackup) {
              backupResources$ = this.apiService.listApplicationResources(appDto.accountId, appDto.appId, 'backup')
            }
            if (shouldRefreshRestore) {
              restoreResources$ = this.apiService.listApplicationResources(appDto.accountId, appDto.appId, 'restore')
            }
            if (shouldRefreshFailback) {
              failbackResources$ = this.apiService.listApplicationResources(appDto.accountId, appDto.appId, 'failback')
            }
            backupResources$ = backupResources$.pipe(
              tap((response) => {
                // Backend latency between editor save and resources being materialized for
                // /resources/backup can return an empty list before the optimistic rows would
                // otherwise be replaced. Keep the optimistic data in that window so the table
                // doesn't flash to the empty state; the next poll picks up the real resources.
                if (
                  this.hasOptimisticResources &&
                  this._backupResources?.length > 0 &&
                  (response.resources?.length ?? 0) === 0
                ) {
                  return
                }
                this._backupResources = response.resources
                this._mergeIntoResourceDtoLookup(response.resources)
                this._backupStatusNextToken = response.statusNextToken
                // Clear optimistic flag — real data has arrived from the API
                this.hasOptimisticResources = false
              })
            )
            restoreResources$ = restoreResources$.pipe(
              tap((response) => {
                this._restoreResources = response.resources
                this._mergeIntoResourceDtoLookup(response.resources)
                this._restoreStatusNextToken = response.statusNextToken
              })
            )
            failbackResources$ = failbackResources$.pipe(
              tap((response) => {
                this._failbackResources = response.resources
                this._mergeIntoResourceDtoLookup(response.resources)
                this._failbackStatusNextToken = response.statusNextToken
              })
            )

            // refresh backup statuses if we are currently backing up or recently completed backup
            let backupStatusChanges$ = of({
              changes: [],
              nextToken: this._backupStatusNextToken,
            } as AppResourceStatusChangesResponseDto)

            if (
              this._activeScopes.has('backup') &&
              (firstPoll || this.currentlyBackingUp || this.backupRecentlyCompleted) &&
              this._backupStatusNextToken
            ) {
              backupStatusChanges$ = this.apiService.listApplicationResourceStatusChanges(
                appDto.accountId,
                appDto.appId,
                'backup',
                this._backupStatusNextToken
              )
            }
            backupStatusChanges$ = backupStatusChanges$.pipe(
              tap((response) => {
                this._backupStatusNextToken = response.nextToken || this._backupStatusNextToken
                this._applyStatusChanges(this._backupStatuses, response.changes)
              })
            )

            // refresh restore statuses if we are currently restoring up or recently completed restoring
            let restoreStatusChanges$ = of({
              changes: [],
              nextToken: this._restoreStatusNextToken,
            } as AppResourceStatusChangesResponseDto)

            if (
              this._activeScopes.has('restore') &&
              (firstPoll || this.currentlyRestoring || this.restoreRecentlyCompleted) &&
              this._restoreStatusNextToken
            ) {
              restoreStatusChanges$ = this.apiService.listApplicationResourceStatusChanges(
                appDto.accountId,
                appDto.appId,
                'restore',
                this._restoreStatusNextToken
              )
            }
            restoreStatusChanges$ = restoreStatusChanges$.pipe(
              tap((response) => {
                this._restoreStatusNextToken = response.nextToken || this._restoreStatusNextToken
                this._applyStatusChanges(this._restoreStatuses, response.changes)
              })
            )

            // refresh failback statuses if we are currently failing back or recently completed failing back
            let failbackStatusChanges$ = of({
              changes: [],
              nextToken: this._failbackStatusNextToken,
            } as AppResourceStatusChangesResponseDto)

            if (
              this._activeScopes.has('failback') &&
              (firstPoll || this.currentlyFailingBack || this.failbackRecentlyCompleted) &&
              this._failbackStatusNextToken
            ) {
              failbackStatusChanges$ = this.apiService.listApplicationResourceStatusChanges(
                appDto.accountId,
                appDto.appId,
                'failback',
                this._failbackStatusNextToken
              )
            }
            failbackStatusChanges$ = failbackStatusChanges$.pipe(
              tap((response) => {
                this._failbackStatusNextToken = response.nextToken || this._failbackStatusNextToken
                this._applyStatusChanges(this._failbackStatuses, response.changes)
              })
            )

            // refresh the recovery points if needed
            let rps$ = of(this.recoveryPoints)
            if (this.lastBackupSuccess > this._recoveryPointsServerTimestamp) {
              rps$ = this.apiService.listApplicationRecoveryPoints(appDto.accountId, appDto.appId)
            }
            rps$ = rps$.pipe(
              tap((rps) => {
                this.setRecoveryPoints(rps)
              })
            )

            // refresh recovery point attempts if needed
            let rpas$ = of(this.recoveryPointAttempts)
            if (this.lastBackupComplete > this._recoveryPointAttemptsServerTimestamp) {
              const dateFrom = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) // last 14 days
              rpas$ = this.apiService.listApplicationRecoveryPointAttempts(appDto.accountId, appDto.appId, dateFrom)
            }
            rpas$ = rpas$.pipe(
              tap((rpas) => {
                this.setRecoveryPointAttempts(rpas)
              })
            )

            // refresh issues if needed
            let issues$ = of(this._applicationIssueDtos)
            if (this._issuesMayBeStale(appDto)) {
              issues$ = this._fetchIssues$(appDto.accountId, appDto.appId)
            }
            issues$ = issues$.pipe(
              tap((issues) => {
                this.setIssues(issues)
              })
            )

            // Refresh the latest failover test result. The undefined check covers apps that never
            // restored, whose lastRestoreRequest never passes the query timestamp, so the header
            // strip would otherwise read "---" forever instead of "Never".
            let latestFailoverTest$ = of(this._latestFailoverTestResult)
            if (
              this._latestFailoverTestResult === undefined ||
              this.lastRestoreRequest > this._latestFailoverTestQueryServerTimestamp
            ) {
              latestFailoverTest$ = this.getLatestFailoverTestResult$().pipe(
                tap(() => {
                  // Coerced to null because lastRestoreRequest is undefined until the first restore,
                  // and a stored undefined would make every later comparison NaN-false.
                  this._latestFailoverTestQueryServerTimestamp = this.lastRestoreRequest ?? null
                })
              )
            }
            latestFailoverTest$ = latestFailoverTest$.pipe(
              tap((result) => {
                this._latestFailoverTestResult = result
              })
            )

            // if this is multi-account, we also refresh the sync pair at this time if we're currently processing
            // or if we haven't polled since the most recent processing completed.
            let spRefresh$ = of(this._syncPair)
            if (
              this.lastBackupStart >= this.lastBackupComplete ||
              this.lastRestoreStart >= this.lastRestoreComplete ||
              this.lastFailbackStart >= this.lastFailbackComplete ||
              this.maxProcessingCompletionDate() > this._syncPairLastQueryServerTimestamp
            ) {
              if (this._syncPair) {
                spRefresh$ = this._syncPair.refreshSyncPair$(this.backupStatus, this.restoreStatus, this.failbackStatus) // shouldn't poll when backupStatus and restoreStatus provided
              } else {
                spRefresh$ = this.syncPairProviderService.loadSyncPair$(
                  this.accountId,
                  this.sourceEndpointUri,
                  this.targetEndpointUri,
                  this.backupStatus,
                  this.restoreStatus,
                  this.failbackStatus
                )
              }
            }
            spRefresh$ = spRefresh$.pipe(
              tap((sp) => {
                this.setSyncPair(sp)
              })
            )

            // For multi-account, we also need to load the associated integrations
            let integrationRefresh$ = of(this._integrationDtos)
            if (!this._integrationDtos) {
              integrationRefresh$ = this.apiService.listApplicationIntegrations(this.accountId, this.applicationId)
              integrationRefresh$ = integrationRefresh$.pipe(
                tap((integrationDtos) => {
                  this._integrationDtos = integrationDtos
                })
              )
            }

            /* This function checks if a job has been completed
             by verifying that both start and end times are defined
             and that the end time is later than the start time.
          */
            const isJobCompleted = (jobStartTime: Date, jobEndTime: Date) => {
              return jobStartTime && jobEndTime && jobEndTime > jobStartTime
            }

            // Combining the generic currentlyActive flag with specific job status checks (backup, restore, failback) for increased reliability.
            const jobsActive =
              this.currentlyActive || // Fast check if it's correctly initialized and indicates a job is currently active
              this.currentlyBackingUp ||
              this.currentlyRestoring ||
              this.currentlyFailingBack

            const jobsComplete =
              isJobCompleted(this.lastBackupStart, this.lastBackupComplete) &&
              isJobCompleted(this.lastRestoreStart, this.lastRestoreComplete) &&
              isJobCompleted(this.lastFailbackStart, this.lastFailbackComplete)

            // Only start the extended issue refresh if we are sure that no jobs are currently active
            return forkJoin([
              rps$,
              rpas$,
              backupResources$,
              restoreResources$,
              failbackResources$,
              issues$,
              latestFailoverTest$,
              spRefresh$,
              integrationRefresh$,
              backupStatusChanges$,
              restoreStatusChanges$,
              failbackStatusChanges$,
            ]).pipe(
              tap(() => {
                if (!jobsActive && jobsComplete) {
                  this._startExtendedIssueRefresh()
                }
              }),
              map((_) => {
                // All of the internal fields were updated in the prior observable via taps.  All
                // we need to do here is compute the next polling interval and return this object
                // so this becomes a feed of updated Application objects.

                // RESTORE_POLL_INTERVAL is shorter than BACKUP_POLL_INTERVAL so check for that case first.
                if (
                  this.currentlyRestoring ||
                  this.currentlyFailingBack ||
                  this.restoreRecentlyCompleted ||
                  this.failbackRecentlyCompleted
                ) {
                  pollIntervals.next(Application.RESTORE_POLL_INTERVAL)
                } else if (
                  this._optimisticReconciliationDeadline !== null &&
                  Date.now() < this._optimisticReconciliationDeadline
                ) {
                  // Post-save reconciliation window: hold the fast cadence for the full 60s
                  // even after currentlyBackingUp lights up, so smaller apps can show real
                  // queued→processing→success transitions instead of jumping straight to
                  // success on the next 15s tick.
                  pollIntervals.next(Application.RESTORE_POLL_INTERVAL)
                } else if (this.currentlyBackingUp || this.backupRecentlyCompleted) {
                  // Window closed (or never opened) — clear the deadline before falling through.
                  this._optimisticReconciliationDeadline = null
                  pollIntervals.next(Application.BACKUP_POLL_INTERVAL)
                } else {
                  this._optimisticReconciliationDeadline = null
                  pollIntervals.next(Application.BACKGROUND_POLL_INTERVAL)
                }

                firstPoll = false
                this._refreshTimestamp = new Date()

                // Re-publish to components that are monitoring applications
                Application.publishSubject$.upsert(this, this.applicationId)

                return this as Application
              })
            ) // forkJoin.pipe()
          }), // switchMap(appDto => ...)
          catchError((err) => {
            // Transient API errors should not kill the polling loop.  Schedule a retry
            // and skip this emission so the outer pollIntervals stream stays alive.
            console.warn('Application polling error, scheduling retry:', err)
            pollIntervals.next(Application.BACKGROUND_POLL_INTERVAL)
            return EMPTY as Observable<Application>
          })
        ) // appDto$.pipe()
      }) // switchMap(_ => ...)
    ) // pollIntervals.pipe()
  }

  refresh$(refreshDto: boolean, poll: boolean = true, forceRefreshResources: boolean = false) {
    /*
      Load the application from the server, potentially polling until any active processing (backup/restore) completes
    */
    // Query the server for the application details, including the details of the sync source and
    // sync pair object.  This may poll until any in-flight backup, restore, or failback operations
    // have completed.  With each query, timestamps are inspected to see if certain data is possibly
    // updated on the server side, which then initiates additional queries to the server for the
    // possibly updated data (_latestFailoverTestResult, recovery points, recovery point attempts,
    // and issues).

    let appRefreshObservable = this._buildRefreshObservable$(refreshDto, forceRefreshResources)

    appRefreshObservable = concat(of(this), appRefreshObservable)

    // If we aren't polling, we only want the first 2 emissions from the observable.
    // The first is the current state, the second is the updated state.
    if (!poll) {
      appRefreshObservable = appRefreshObservable.pipe(take(2))
    }

    return appRefreshObservable
  }

  refreshRecoveryPoints$() {
    return this.apiService.listApplicationRecoveryPoints(this.accountId, this.applicationId).pipe(
      switchMap((rpsDto) => {
        this.setRecoveryPoints(rpsDto)
        return of(null)
      })
    )
  }

  private maxProcessingCompletionDate() {
    // We look at the completion dates of backup, restore, and failback to determine the latest point in time that
    // new issues could have been created.  That is then used in the refresh logic to determine if we need to refresh
    // issues again.
    return maxDate(maxDate(this.lastBackupComplete, this.lastRestoreComplete), this.lastFailbackComplete)
  }

  private buildSyntheticDeleteIssue() {
    const scope = this.lastBackupSuccess > this.deletedAt ? RaisedIssueScope.RESTORE : RaisedIssueScope.BACKUP

    const syntheticIssue = new DeleteFailedIssue()
    // Generate a synthetic issue ID for the delete failure
    const syntheticIssueId = `synthetic-delete-failure-${this.applicationId}`
    const raisedIssue = new RaisedIssue(syntheticIssueId, null, scope, syntheticIssue, null)
    return adaptApplicationIssueDto(raisedIssue)
  }

  private setIssues(issues: Array<ApplicationIssueDto>) {
    // Show a synthetic delete issue if:
    // - the application was deleted
    // - there are no other issues
    // - the application is not currently active
    // - the backend reports that restore resources still remain after the last restore
    // - sufficient time has passed since deletion to indicate actual failure
    const timeSinceDeletion = this.deletedAt ? Date.now() - this.deletedAt.getTime() : 0
    const deletionTimeoutMs = 30 * 60 * 1000 // 30 minutes

    if (
      issues &&
      issues.length == 0 &&
      this.deleted &&
      !this.currentlyActive &&
      this.lastRestoreResourcesRemain &&
      timeSinceDeletion > deletionTimeoutMs
    ) {
      issues.push(this.buildSyntheticDeleteIssue())
    }

    this._applicationIssueDtos = issues
    this._lastSeenProcessingCompletion = this.maxProcessingCompletionDate()
    this._lastSeenIssueRevision = this.applicationDto?.issueRevision ?? null
    this._lastSeenSourceIssueRevision = this.applicationDto?.sourceIssueRevision ?? null
    // Invalidate unconditionally: a poll that skipped the fetch can still mutate the
    // existing array in place (the synthetic delete issue above), so array identity
    // isn't a reliable signal that nothing changed.
    this._issueSections = null
  }

  private setRecoveryPointAttempts(rpas: Array<RecoveryPointAttemptDto>) {
    this._recoveryPointAttempts = rpas
    this._recoveryPointAttemptsServerTimestamp = this.lastBackupComplete
  }

  private setRecoveryPoints(rps: Array<RecoveryPointDto>) {
    this._recoveryPoints = rps
    this._recoveryPointsServerTimestamp = this.lastBackupComplete
  }

  filterEmptyNameRules(rules: BaseRuleDto[]): BaseRuleDto[] {
    return rules.filter((rule) => {
      // Filter out rules with empty names
      if (rule.ruleType === 'tag' && (rule as TagRuleDto).name === '') {
        return false
      }
      // Filter out ResourceTypeRule with empty resourceType
      if (rule.ruleType === 'resourceType' && (rule as ResourceTypeRuleDto).resourceType === '') {
        return false
      }
      // Recursively filter AllRuleDto
      if (rule.ruleType === 'all') {
        ;(rule as AllRuleDto).rules = this.filterEmptyNameRules((rule as AllRuleDto).rules)
        return (rule as AllRuleDto).rules.length > 0 // Keep the AllRule only if it has non-empty rules
      }
      return true
    })
  }

  update$(
    updatedName: string,
    updatedRpo: number,
    updatedNotificationEmails: Array<string>,
    updatedSelectionRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    updatedExcludingRulesOrAllRule: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    updatedSelectionArns: Set<Arn>,
    updatedExcludeWeakArns: Set<Arn>,
    integrationIds: Set<string>,
    identityProviderId: string = null
  ) {
    // Process the updatedSelectionRules array to create a unified array of DTOs
    let selectionRules: Array<BaseRuleDto> = updatedSelectionRules
      .map((rule) => {
        if ('rules' in rule) {
          // It's an AllRule
          return createAllRuleDto(rule as AllRule)
        } else if (rule instanceof ResourceTypeRule) {
          // It's a ResourceTypeRule
          return createResourceTypeRuleDto(rule)
        } else if (rule instanceof ResourceGroupRule) {
          // It's a ResourceGroupRule
          return createResourceGroupRuleDto(rule)
        } else {
          // It's a Tag
          return createTagRuleDto(rule as Tag)
        }
      })
      .filter((rule) => {
        // Filter out empty AllRules
        if (rule.ruleType === 'all') {
          ;(rule as AllRuleDto).rules = this.filterEmptyNameRules((rule as AllRuleDto).rules)
          return (rule as AllRuleDto).rules.length > 0 // Keep the AllRule only if it has non-empty rules
        }
        return true // Keep everything else
      })

    // Process the updatedExcludingRulesOrAllRule array to create exclude rules
    let excludeRules: Array<BaseRuleDto> = updatedExcludingRulesOrAllRule
      .map((rule) => {
        if ('rules' in rule) {
          // It's an AllRule
          return createAllRuleDto(rule as AllRule, 'exclude')
        } else if (rule instanceof ResourceTypeRule) {
          // It's a ResourceTypeRule
          return createResourceTypeRuleDto(rule, 'exclude')
        } else if (rule instanceof ResourceGroupRule) {
          // It's a ResourceGroupRule
          return createResourceGroupRuleDto(rule, 'exclude')
        } else {
          // It's a Tag
          return createTagRuleDto(rule as Tag, 'exclude')
        }
      })
      .filter((rule) => {
        // Filter out empty AllRules
        if (rule.ruleType === 'all') {
          ;(rule as AllRuleDto).rules = this.filterEmptyNameRules((rule as AllRuleDto).rules)
          return (rule as AllRuleDto).rules.length > 0 // Keep the AllRule only if it has non-empty rules
        }
        return true // Keep everything else
      })

    // Combine include and exclude rules
    selectionRules = [...selectionRules, ...excludeRules]

    // Append Arn and ExcludeWeakArn rules to selectionRules
    const updatedArnRule = createArnRuleDto(updatedSelectionArns)
    if (updatedArnRule.arns.length > 0) {
      selectionRules.push(updatedArnRule as BaseRuleDto)
    }

    const updatedExcludeWeakArnRule = createExcludeWeakArnRuleDto(updatedExcludeWeakArns)
    if (updatedExcludeWeakArnRule.arns.length > 0) {
      selectionRules.push(updatedExcludeWeakArnRule as BaseRuleDto)
    }

    let integrationsToAdd = diffSets(integrationIds, this.integrationIds)
    let integrationsToDel = diffSets(this.integrationIds, integrationIds)
    let updates = [] as Array<Observable<null | ApplicationIntegrationDto | ApplicationDto>>

    // Pass the unified selectionRules array to the updateApplication() method
    updates.push(
      this.apiService.updateApplication(
        this.applicationDto,
        updatedName,
        updatedRpo,
        updatedNotificationEmails,
        selectionRules,
        identityProviderId
      )
    )

    integrationsToAdd.forEach((intId) => {
      updates.push(this.apiService.createApplicationIntegration(this.accountId, this.applicationId, intId))
    })

    integrationsToDel.forEach((intId) => {
      updates.push(this.apiService.deleteApplicationIntegration(this.accountId, this.applicationId, intId))
    })
    return forkJoin(updates).pipe(
      switchMap((responses) => {
        let appDto = responses[0] as ApplicationDto
        this.applicationDto = appDto
        return this.apiService.listApplicationIntegrations(this.accountId, this.applicationId)
      }),
      map((integrationDtos) => {
        this._integrationDtos = integrationDtos
        return this
      })
    )
  }

  public loadSourceResources$() {
    if (this._syncSource) {
      return of(this._syncSource.resources)
    } else {
      return this.syncSourceProviderService.loadSyncSource$(this.accountId, this.sourceEndpointUri).pipe(
        map((ss) => {
          this._syncSource = ss
          return ss.resources
        })
      )
    }
  }

  public rescanSourceResources$(additionalIntegrationIds: Set<string>) {
    if (this._syncSource) {
      return this._syncSource.rescanSourceResources$(additionalIntegrationIds, this.identityProviderId).pipe(
        map((ss) => {
          this._syncSource = ss
          return this
        })
      )
    } else {
      return this.syncSourceProviderService.loadSyncSource$(this.accountId, this.sourceEndpointUri).pipe(
        map((ss) => {
          this._syncSource = ss
          return this
        })
      )
    }
  }

  static updateApplicationSyncPhases$(
    primaryApplication: Application,
    restoreRequests: Array<RestoreRequest>,
    syncPhase: SyncPhaseDto
  ) {
    // If we're running a test, override the networkSandboxEnabled flag
    if (syncPhase == SyncPhaseDto.FAILOVER_TEST) {
      restoreRequests.forEach((req) => (req.application._networkSandboxOverride = !!req.networkSandboxEnabled))
    }

    // ARP-6871: Only send the user-selected applications in the restore request.
    // Previously, the UI would query all applications and implicitly include every non-standby
    // app in the same region pair. This caused 403s when the user didn't have permission on
    // those extra apps (e.g. OPENLANE's RBAC setup with per-app admin roles).
    //
    // The backend's PlanRestore step function (ARP-1574, Feb 2024) automatically discovers
    // cross-account dependencies and builds the full restore graph during app grouping,
    // so the UI no longer needs to pad the request with unselected apps.

    // ARP-6718: Optimistic sync-phase hint — set synchronously here (before the
    // initiateRestore round trip) so the UI reflects the target phase the moment the
    // user clicks, not after the API response returns. The syncPhase getter clears this
    // automatically once the DTO carries restoreStatus.lastRequestSyncPhase.
    restoreRequests.forEach((req) => {
      req.application._optimisticSyncPhaseOverride = syncPhase
      // Latch the request time too, so the resource-status masking survives a poll that
      // hasn't picked up last_restore_request yet.
      req.application.latchRestoreRequest()
      Application.publishSubject$.upsert(req.application, req.application.applicationId)
    })
    const appRestoreDtos = new Map(restoreRequests.map((req) => adaptRestoreRequest(req, syncPhase)))
    const restoreRequestDto: RestoreRequestDto = { apps: mapToObject(appRestoreDtos) }

    return primaryApplication.apiService.initiateRestore(primaryApplication.accountId, restoreRequestDto).pipe(
      tap((restoreResponseDto) => {
        restoreResponseDto.apps.forEach((appDto) => {
          // Find the application for this DTO and update its DTO, then publish it.
          // The optimistic override was already set synchronously above; it will be
          // dropped automatically once the DTO shows the same phase via
          // restoreStatus.lastRequestSyncPhase or syncPhase (see _dtoReflectsOptimistic).
          let appRequest = restoreRequests.find((req) => req.application.applicationId === appDto.appId)
          if (appRequest) {
            appRequest.application.applicationDto = appDto
            appRequest.application.confirmRestoreRequestLatch(appDto.restoreStatus?.lastRequest)
            Application.publishSubject$.upsert(appRequest.application, appRequest.application.applicationId)
          }
        })
      }),
      catchError((err) => {
        // Roll back the optimistic phase override on every request whose
        // initiateRestore call failed (409, 403, network, timeout). Without
        // this the UI sticks on "Initiating..." forever — the DTO never
        // matches the override, so _dtoReflectsOptimistic stays false.
        restoreRequests.forEach((req) => {
          req.application._optimisticSyncPhaseOverride = null
          req.application.clearRestoreRequestLatch()
          Application.publishSubject$.upsert(req.application, req.application.applicationId)
        })
        throw err
      }),
      switchMap((_) => of(primaryApplication))
    )
  }

  static initiateFailback$(primaryApplication: Application, allApplications: Array<Application>) {
    return primaryApplication.apiService
      .initiateMultiAccountFailback(
        primaryApplication.accountId,
        allApplications.map((app) => app.applicationId)
      )
      .pipe(switchMap((_failbackResponse) => primaryApplication.refresh$(true)))
  }

  submitFailoverTestResults$(success: boolean, notes: string) {
    let now = new Date()
    // Stamp both outcomes so a failed conclude doesn't leave a stale pass on screen, and roll back
    // if the POST fails: a conclude that never reached the server leaves lastRestoreRequest where it
    // was, so the refresh gate would never correct the optimistic value.
    const priorResult = this._latestFailoverTestResult
    this._latestFailoverTestResult = {
      accountId: this.accountId,
      appId: this.applicationId,
      timestamp: now,
      sourceEndpointUri: this.sourceEndpointUri,
      sourceAwsAccountId: this.sourceAwsAccountId,
      sourceRegion: this.sourceRegion,
      targetEndpointUri: this.targetEndpointUri,
      targetAwsAccountId: this.targetAwsAccountId,
      targetRegion: this.targetRegion,
      success,
      notes,
    }

    return this.apiService
      .submitFailoverTestResults(
        this.accountId,
        this.applicationId,
        this.sourceEndpointUri,
        this.sourceAwsAccountId,
        this.sourceRegion,
        this.targetEndpointUri,
        this.targetAwsAccountId,
        this.targetRegion,
        now,
        success,
        notes
      )
      .pipe(
        tap({ error: () => (this._latestFailoverTestResult = priorResult) }),
        map((_) => this)
      )
  }

  getLatestFailoverTestResult$() {
    return this.apiService.listFailoverTestResults(this.accountId, this.applicationId, null, null, true, 1).pipe(
      map((testResults) => {
        if (testResults.length > 0) {
          return testResults[0]
        }
        return null
      })
    )
  }

  // True only when all currently active scopes have their resources fetched
  get isLoaded(): boolean {
    for (const scope of this._activeScopes) {
      if (scope === 'backup' && !this._backupResources) return false
      if (scope === 'restore' && !this._restoreResources) return false
      if (scope === 'failback' && !this._failbackResources) return false
    }
    return this._activeScopes.size > 0
  }

  /** Inject optimistic backup resources so the table shows new resources immediately
   *  after an editor save, with a pending/spinner state until the backend responds.
   *  Builds ApplicationResourceDto entries from source resource metadata. */
  setOptimisticBackupResources(sourceResources: AwsSourceResourceDto[], matchingArns: Set<string>): void {
    // Build optimistic DTOs for all selected resources — status is omitted so the
    // table renders a spinner instead of a status badge
    const optimisticResources: ApplicationResourceDto[] = sourceResources
      .filter((r) => matchingArns.has(r.arn))
      .map((r) => ({
        arn: r.arn,
        type: r.type,
        variant: r.variant,
        source: {
          id: r.id,
          name: r.name,
          displayName: r.displayName,
          resourceGroup: r.resourceGroup,
          parentArn: r.parentArn,
        },
        // No target or status — these are optimistic placeholders
      }))

    this._backupResources = optimisticResources
    this._mergeIntoResourceDtoLookup(optimisticResources)
    this._backupStatuses = new Map()
    // Preserve _backupStatusNextToken so the first post-save poll's status-changes
    // call still runs (gated on `firstPoll && _backupStatusNextToken`). Nulling it
    // leaves new resources without status pills until BACKGROUND_POLL_INTERVAL elapses.
    // Invalidate the resource cache so getBackupResources() rebuilds from the optimistic data
    this._appResourceCache = new Map()
    this._refreshTimestamp = new Date()

    // Invalidate other scopes so they refresh when activated
    this._restoreResources = null
    this._failbackResources = null
    this._restoreStatusNextToken = null
    this._failbackStatusNextToken = null

    // Ensure 'backup' is in the active scopes set so the next poll actually
    // refreshes these optimistic rows with real data. If the user was on
    // Recovery before saving, setActiveScope('restore') cleared the backup
    // scope and reconciliation would otherwise never happen.
    this._activeScopes.add('backup')

    // Flag so loadApplication$ preserves this data instead of clearing it
    this.hasOptimisticResources = true

    // Open the rapid-poll reconciliation window. 60s is enough for the backend to register
    // the request and flip currentlyBackingUp; once that happens BACKUP_POLL_INTERVAL takes
    // over and this branch stops firing.
    this._optimisticReconciliationDeadline = Date.now() + 60_000
  }

  /** Clear all cached resource data so the next poll cycle fetches fresh from the API.
   *  Called before navigation back to app overview after an editor save, so the
   *  first cached emission from loadApplication$ doesn't show stale resources. */
  invalidateResourceCaches(): void {
    this._backupResources = null
    this._restoreResources = null
    this._failbackResources = null
    this._backupStatuses = new Map()
    this._restoreStatuses = new Map()
    this._failbackStatuses = new Map()
    this._backupStatusNextToken = null
    this._restoreStatusNextToken = null
    this._failbackStatusNextToken = null
    this._appResourceCache = new Map()
  }

  // Switches to a single active scope — clears all others so only this tab's resources are polled
  setActiveScope(scope: ResourceScope): void {
    this._activeScopes.clear()
    this._activeScopes.add(scope)
  }

  // Ride-along scope addition without disturbing the primary scope. Used when a button or
  // affordance in one tab depends on resources from another (e.g., the FAILBACK button on
  // the Recovery Environment tab needs failback resources before the user navigates to the
  // Failback sub-tab).
  addActiveScope(scope: ResourceScope): void {
    this._activeScopes.add(scope)
  }

  // Check if a specific scope has fetched resources (used by components to gate status computation)
  isScopeLoaded(scope: ResourceScope): boolean {
    switch (scope) {
      case 'backup':
        return !!this._backupResources
      case 'restore':
        return !!this._restoreResources
      case 'failback':
        return !!this._failbackResources
      default:
        return false
    }
  }

  get latestFailoverTestResult() {
    return this._latestFailoverTestResult
  }

  // Cross-scope check — true if any scope has fetched a non-empty resource list
  get hasResources() {
    return (
      this._backupResources?.length > 0 || this._restoreResources?.length > 0 || this._failbackResources?.length > 0
    )
  }

  get backupResourceDtos() {
    return this._backupResources
  }

  get restoreResourceDtos() {
    return this._restoreResources
  }

  get failbackResourceDtos() {
    return this._failbackResources
  }

  /** Union of every resource DTO we've ever seen across scopes, keyed by ARN.
   *  Survives scope cache invalidation (inactive scopes' arrays are nulled every
   *  tab switch) so issue enrichment works for any scope regardless of which tab
   *  the user is currently viewing.
   *
   *  Experience without this:
   *  On the Issues tab (or anywhere issues are rendered), any issue whose scope
   *  isn't the currently-active scope lost its resource name / resource type
   *  enrichment. E.g. open the app on Recovery, then switch to Issues — backup
   *  issues would suddenly render with missing resource type/name (or fall back
   *  to raw ARNs) because the backup resource array had been nulled on tab
   *  switch. Same visual glitch in reverse when going from Backup → Issues. */
  get allKnownResourceDtos(): ApplicationResourceDto[] {
    return Array.from(this._resourceDtoLookup.values())
  }

  /** Called from every scope's fetch/status-update path (and the optimistic backup
   *  seeding) so the lookup keeps accumulating DTOs regardless of which scope is
   *  currently active. Upserts by ARN — newer DTO wins for an already-known resource. */
  private _mergeIntoResourceDtoLookup(resources: ApplicationResourceDto[] | null): void {
    if (!resources) return
    for (const res of resources) {
      if (res?.arn) this._resourceDtoLookup.set(res.arn, res)
    }
  }

  get sourceResources() {
    return this._syncSource && this._syncSource.resources
  }

  buildBackupResources(): Array<ApplicationResource> {
    // True for ~30s after the latest backup successfully completed. Lets rows without
    // a per-resource status yet render as PENDING (transient lag) instead of NO_STATUS.
    const processRecentlyCompleted =
      !this.currentlyBackingUp &&
      this.backupRecentlyCompleted &&
      !!this.lastBackupSuccess &&
      (!this.lastBackupFailure || this.lastBackupSuccess >= this.lastBackupFailure)
    return this._buildResourcesForScope(
      this._backupResources || [],
      this._backupStatuses,
      this.currentlyBackingUp,
      this.backupHorizon,
      this.recoveryPointIssues,
      'backup',
      processRecentlyCompleted
    )
  }

  buildRestoreResources(): Array<ApplicationResource> {
    const processRecentlyCompleted =
      !this.currentlyRestoring &&
      this.restoreRecentlyCompleted &&
      !!this.lastRestoreSuccess &&
      (!this.lastRestoreFailure || this.lastRestoreSuccess >= this.lastRestoreFailure)
    return this._buildResourcesForScope(
      this._restoreResources || [],
      this._restoreStatuses,
      this.currentlyRestoring,
      this.restoreHorizon,
      this.restoreIssues,
      'restore',
      processRecentlyCompleted
    )
  }

  buildFailbackResources(): Array<ApplicationResource> {
    const processRecentlyCompleted =
      !this.currentlyFailingBack &&
      this.failbackRecentlyCompleted &&
      !!this.lastFailbackSuccess &&
      (!this.lastFailbackFailure || this.lastFailbackSuccess >= this.lastFailbackFailure)
    return this._buildResourcesForScope(
      this._failbackResources || [],
      this._failbackStatuses,
      this.currentlyFailingBack,
      this.failbackHorizon,
      this.failbackIssues,
      'failback',
      processRecentlyCompleted
    )
  }

  // Picks the later of two statuses for one ARN; the polled entry wins a tie or a missing timestamp.
  private static _newerStatus(
    polled: AppResourceStatusChangeDto | null,
    inline: AppResourceStatusChangeDto | null
  ): AppResourceStatusChangeDto | null {
    if (!polled || !inline) return polled ?? inline
    return inline.timestamp > polled.timestamp ? inline : polled
  }

  // Builds ApplicationResource view models for a single scope, merging DTO data with polled statuses and issues
  private _buildResourcesForScope(
    appResources: ApplicationResourceDto[],
    statuses: Map<string, AppResourceStatusChangeDto>,
    currentlyProcessing: boolean,
    processingHorizon: Date,
    scopedIssues: RaisedIssue[],
    scope: 'backup' | 'restore' | 'failback' = 'restore',
    processRecentlyCompleted: boolean = false
  ): Array<ApplicationResource> {
    const resources = new Array<ApplicationResource>()
    const targetResources = this._syncPair ? this._syncPair.targetResourceMap() : new Map<Arn, AwsTargetResourceDto>()
    const issues = new Map<Arn, Set<RaisedIssue>>()
    for (const issue of scopedIssues) {
      if (!issues.has(issue.arn)) {
        issues.set(issue.arn, new Set<RaisedIssue>())
      }
      issues.get(issue.arn).add(issue)
    }

    for (const res of appResources) {
      const sourceArn = res.arn
      const targetResource = targetResources.get(sourceArn)
      // Newest timestamp wins: the cursor can miss a change, so a full refetch may carry a newer status.
      // Stays null when neither side has one, so _computeStatus never sees a truthy empty object.
      const polledStatus = statuses.get(sourceArn) ?? null
      const inlineStatus = res.status ? ({ arn: res.arn, ...res.status } as AppResourceStatusChangeDto) : null
      const latestStatus = Application._newerStatus(polledStatus, inlineStatus)
      const resourceIssues = issues.get(sourceArn) || new Set<RaisedIssue>()

      const appResource = new ApplicationResource(
        res,
        targetResource,
        latestStatus,
        resourceIssues,
        currentlyProcessing,
        processingHorizon,
        this.type,
        scope,
        processRecentlyCompleted
      )
      resources.push(appResource)
    }

    return resources
  }

  updateBackupResourceStatus(status: ResourceStatus, arns: Set<Arn>) {
    this._updateCachedResourceStatus('backup', status, arns)
  }

  updateRestoreResourceStatus(status: ResourceStatus, arns: Set<Arn>) {
    this._updateCachedResourceStatus('restore', status, arns)
  }

  updateFailbackResourceStatus(status: ResourceStatus, arns: Set<Arn>) {
    this._updateCachedResourceStatus('failback', status, arns)
  }

  // Optimistically updates cached view models so the UI reflects status changes before the next poll
  private _updateCachedResourceStatus(scope: ResourceScope, status: ResourceStatus, arns: Set<Arn>) {
    if (this._appResourceCache.has(scope)) {
      this._appResourceCache.get(scope)[1].forEach((res) => {
        if (arns.has(res.resourceArn)) {
          res.status = status
        }
      })
    }
  }

  getBackupResources(filterText?: string): ApplicationResource[] {
    return this._getResourcesForScope('backup', () => this.buildBackupResources(), filterText)
  }

  getRestoreResources(filterText?: string): ApplicationResource[] {
    return this._getResourcesForScope('restore', () => this.buildRestoreResources(), filterText)
  }

  getFailbackResources(filterText?: string): ApplicationResource[] {
    return this._getResourcesForScope('failback', () => this.buildFailbackResources(), filterText)
  }

  // Returns cached or freshly built ApplicationResource[] for a scope, with optional text filter.
  // Only returns [] when the scope's raw resource array is null (not yet fetched).
  // An empty array means "fetched, no resources" and still goes through buildFn
  // so the cache stays consistent.
  private _getResourcesForScope(
    scope: ResourceScope,
    buildFn: () => Array<ApplicationResource>,
    filterText?: string
  ): ApplicationResource[] {
    // Scope-specific null check: null = not yet fetched, [] = fetched but empty
    const scopeResources =
      scope === 'backup'
        ? this._backupResources
        : scope === 'restore'
          ? this._restoreResources
          : this._failbackResources
    if (scopeResources === null || scopeResources === undefined) {
      return []
    }

    const refreshTimestamp = this._refreshTimestamp
    if (!this._appResourceCache.has(scope) || this._appResourceCache.get(scope)[0] != refreshTimestamp) {
      this._appResourceCache.set(scope, [refreshTimestamp, buildFn()])
    }
    let resources = this._appResourceCache.get(scope)[1]
    resources = resources.filter((appRes) => {
      return (
        appRes.resourceTypeDetails.displayInStatus &&
        (!filterText ||
          filterText.length === 0 ||
          appRes.displayResourceType.toLowerCase().search(filterText.toLowerCase()) >= 0 ||
          appRes.resourceId.toLowerCase().search(filterText.toLowerCase()) >= 0 ||
          appRes.sourceResourceName.toLowerCase().search(filterText.toLowerCase()) >= 0 ||
          appRes.targetResourceName.toLowerCase().search(filterText.toLowerCase()) >= 0)
      )
    })

    return resources
  }

  /*
    Mark the application as deleted instead of removing it:
    applicationDto.deleted = true; and updates the publishSubject$ cache
    This will keep it in the UI but give it deleted funtionality
  */
  delete$() {
    let applicationId = this.applicationId
    return this.apiService.deleteApplication(this.accountId, applicationId).pipe(
      tap(() => {
        this.setAppDeleted(true)
        Application.publishSubject$.upsert(this, applicationId)
      })
    )
  }

  get deleted(): boolean {
    return this.applicationDto.deleted
  }

  get restoreTrigger(): RestoreTriggerDto | null {
    return this.applicationDto?.restoreTrigger
  }

  private setAppDeleted(value: boolean) {
    this.applicationDto.deleted = value
  }
  get backupStatus() {
    return this.applicationDto.backupStatus
  }

  get restoreStatus() {
    return this.applicationDto.restoreStatus
  }

  get failbackStatus() {
    return this.applicationDto.failbackStatus
  }

  get applicationId() {
    return this.applicationDto.appId
  }

  get accountId() {
    return this.applicationDto.accountId
  }

  get name() {
    return this.applicationDto.name
  }

  get type() {
    return this.applicationDto.type
  }

  get isAzure(): boolean {
    return this.applicationDto.type === ApplicationType.AZURE
  }

  get sourceEndpointUri() {
    return this.applicationDto.sourceEndpointUri
  }

  get targetEndpointUri() {
    return this.applicationDto.targetEndpointUri
  }

  // Derives Azure source subscription display name from backup resources,
  // which are loaded on the first refresh poll — no extra API call needed.
  // The subscription resource (Microsoft.Resources/subscriptions) is included in the
  // listApplicationResources response with source.displayName populated.
  get sourceSubscriptionName(): string | null {
    if (this.applicationDto.type !== ApplicationType.AZURE || !this._backupResources) return null
    const [, sourceSubId] = this.sourceEndpointUri.split(':')
    // source.id is a full Azure path (e.g. "/subscriptions/uuid") — match on the
    // full /subscriptions/ prefix + ID to avoid false positives from embedded GUIDs.
    const sub = this._backupResources.find(
      (r) => r.type === ResourceType.azureSubscription && r.source?.id?.endsWith(`/subscriptions/${sourceSubId}`)
    )
    return sub?.source?.displayName || null
  }

  // Derives Azure target subscription display name from _syncPair target resources.
  // Unlike source, target data isn't available in _backupResources (the listApplicationResources
  // call uses includeTargetResources=false for performance). _syncPair loads when the
  // resources tab is visited or during active processing, so the target name appears then.
  get targetSubscriptionName(): string | null {
    if (this.applicationDto.type !== ApplicationType.AZURE || !this._syncPair) return null
    const [, targetSubId] = this.targetEndpointUri.split(':')
    // Resource id is a full Azure path (e.g. "/subscriptions/uuid") — match on the
    // full /subscriptions/ prefix + ID to avoid false positives from embedded GUIDs.
    const sub = this._syncPair.targetResources.find(
      (r) => r.type === ResourceType.azureSubscription && r.id.endsWith(`/subscriptions/${targetSubId}`)
    )
    return sub?.displayName || null
  }

  get sourceAwsAccountId() {
    return this.applicationDto.sourceAwsAccountId
  }

  get sourceRegion() {
    return this.applicationDto.sourceRegion
  }

  get targetAwsAccountId() {
    return this.applicationDto.targetAwsAccountId
  }

  get targetRegion() {
    return this.applicationDto.targetRegion
  }

  get identityProviderId() {
    return this.applicationDto.identityProviderId
  }

  get selectionRules() {
    return this.applicationDto.selectionRules.map((dto: BaseRuleDto) => {
      const rtd = ruleDtoToRule(dto)
      return rtd
    })
  }

  get matchingArns() {
    let matchingArns = new Set<Arn>()
    for (let rule of this.selectionRules) {
      if (rule instanceof ArnRule) {
        rule.arnSet.forEach((arn) => matchingArns.add(arn))
      }
    }
    return matchingArns
  }

  get suppressedArns() {
    let suppressedArns = new Set<Arn>()
    for (let rule of this.selectionRules) {
      if (rule instanceof ExcludeWeakArnRule) {
        rule.arnSet.forEach((arn) => suppressedArns.add(arn))
      }
    }
    return suppressedArns
  }

  /** This code handles what display in the UI */
  get matchingRules() {
    let matchingRules = new Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>()
    for (let i = 0; i < this.selectionRules.length; i++) {
      const rule = this.selectionRules[i]
      const ruleDto = this.applicationDto.selectionRules[i]

      // Only include rules without action='exclude' (i.e., include rules)
      if (ruleDto && (ruleDto as any).action === 'exclude') {
        continue // Skip exclude rules
      }

      // Special handling for AllRule - check if it has exclude inner rules
      if (ruleDto && ruleDto.ruleType === 'all' && rule instanceof AllRule) {
        const allRuleDto = ruleDto as AllRuleDto
        const hasExcludeInnerRules = allRuleDto.rules.some((innerRule) => (innerRule as any).action === 'exclude')

        if (hasExcludeInnerRules) {
          // If this AllRule has exclude inner rules, only include the non-exclude rules
          const nonExcludeRules = allRuleDto.rules.filter((innerRule) => (innerRule as any).action !== 'exclude')
          if (nonExcludeRules.length > 0) {
            // Create a new AllRule with only the include rules
            const filteredRule = new AllRule({
              ruleType: 'all',
              rules: nonExcludeRules,
            })
            matchingRules.push(filteredRule as any)
          }
          continue // Skip processing this rule further
        }
        // If no exclude inner rules, fall through to normal processing
      }

      if (rule instanceof TagRule) {
        matchingRules.push({ tagName: rule.tagName, tagValue: rule.tagValue || null, operator: rule.operator } as any)
      } else if (rule instanceof AllRule) {
        matchingRules.push(rule)
      } else if (rule instanceof ResourceTypeRule) {
        matchingRules.push(rule)
      } else if (rule instanceof ResourceGroupRule) {
        matchingRules.push(rule)
      }
    }
    return matchingRules
  }

  get excludingRules() {
    let excludingRules = new Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>()
    for (let i = 0; i < this.selectionRules.length; i++) {
      const rule = this.selectionRules[i]
      const ruleDto = this.applicationDto.selectionRules[i]

      // Only include rules with action='exclude'
      if (!ruleDto || (ruleDto as any).action !== 'exclude') {
        // For AllRule, check if any inner rules have action='exclude'
        if (ruleDto && ruleDto.ruleType === 'all' && rule instanceof AllRule) {
          const allRuleDto = ruleDto as AllRuleDto
          const hasExcludeInnerRules = allRuleDto.rules.some((innerRule) => (innerRule as any).action === 'exclude')
          if (hasExcludeInnerRules) {
            // Filter the AllRule to only include exclude rules
            const filteredRule = new AllRule({
              ruleType: 'all',
              rules: allRuleDto.rules.filter((innerRule) => (innerRule as any).action === 'exclude'),
            })
            excludingRules.push(filteredRule as any)
            continue
          }
        }
        continue // Skip non-exclude rules
      }

      if (rule instanceof TagRule) {
        excludingRules.push({ tagName: rule.tagName, tagValue: rule.tagValue || null, operator: rule.operator } as any)
      } else if (rule instanceof AllRule) {
        excludingRules.push(rule)
      } else if (rule instanceof ResourceTypeRule) {
        excludingRules.push(rule)
      } else if (rule instanceof ResourceGroupRule) {
        excludingRules.push(rule)
      }
    }
    return excludingRules
  }

  get rpo() {
    return this.applicationDto.rpo
  }

  get notificationEmails() {
    return this.applicationDto.notificationEmails
  }

  get integrationIds() {
    return new Set<string>(this._integrationDtos.map((dto) => dto.integrationId))
  }

  /**
   * Effective sync phase for the UI. Resolved in priority order:
   *
   * 1. Backend-confirmed in-flight transition. When the most recent restore request
   *    is still pending (lastRequest newer than both lastSuccess and lastFailure),
   *    the backend tells us what phase it targets via restoreStatus.lastRequestSyncPhase —
   *    use that. Survives page refresh because it's DTO-sourced.
   *
   * 2. Optimistic override. Bridges the click → first-poll window where the DTO
   *    hasn't been refreshed yet. Used only if the DTO doesn't already agree
   *    (neither dto.syncPhase nor dto.restoreStatus.lastRequestSyncPhase matches the
   *    override) — so we never "pre-announce" a phase the backend hasn't logged.
   *
   * 3. Steady state. No pending transition — show the DTO's current sync phase.
   *
   * ARP-6718: customers saw a stale "Performing test failover" after concluding a
   * test because the previous in-memory override was lost on refresh and the
   * backend took ~6 min to flip syncPhase. The backend-confirmed layer above fixes
   * that case persistently; the optimistic layer keeps the click feel snappy.
   */
  get syncPhase(): SyncPhaseDto {
    if (this._isRestoreRequestPending() && this.applicationDto.restoreStatus?.lastRequestSyncPhase) {
      return this.applicationDto.restoreStatus.lastRequestSyncPhase
    }
    if (this._optimisticSyncPhaseOverride) {
      if (this._dtoReflectsOptimistic()) {
        // DTO caught up — clear stale phase hint from previous action
        this._optimisticSyncPhaseOverride = null
      } else {
        return this._optimisticSyncPhaseOverride
      }
    }
    return this.applicationDto.syncPhase
  }

  /**
   * True when the most recent restore request hasn't concluded yet.
   *
   * Requires the backend to still be tracking the request as active. Timestamp
   * dominance alone isn't enough: a request that never resolved (no
   * lastSuccess/lastFailure update) would otherwise read as "pending" forever
   * and mask the actual dto.syncPhase. The backend's `active` flag is the
   * authoritative "still in flight" signal.
   */
  private _isRestoreRequestPending(): boolean {
    if (!this.applicationDto.restoreStatus?.active) return false
    const request = this.applicationDto.restoreStatus?.lastRequest?.getTime() ?? 0
    if (!request) return false
    const success = this.applicationDto.restoreStatus?.lastSuccess?.getTime() ?? 0
    const failure = this.applicationDto.restoreStatus?.lastFailure?.getTime() ?? 0
    return request > Math.max(success, failure)
  }

  /** True when the DTO has caught up to our optimistic hint (backend sees the same target). */
  private _dtoReflectsOptimistic(): boolean {
    const dto = this.applicationDto
    if (dto.syncPhase === this._optimisticSyncPhaseOverride) return true
    // lastRequestSyncPhase lands before restoreStatus.active flips true, so
    // dropping the override here flickers back to dto.syncPhase until the
    // request actually resolves.
    if (dto.restoreStatus?.lastRequestSyncPhase === this._optimisticSyncPhaseOverride) {
      const request = dto.restoreStatus.lastRequest?.getTime() ?? 0
      const success = dto.restoreStatus.lastSuccess?.getTime() ?? 0
      const failure = dto.restoreStatus.lastFailure?.getTime() ?? 0
      return Math.max(success, failure) >= request
    }
    return false
  }

  get dto() {
    return { ...this.applicationDto }
  }

  get createdAt() {
    return this.applicationDto.createdAt
  }

  get networkSandboxEnabled(): boolean {
    return this._networkSandboxOverride === null
      ? this.applicationDto.failoverTestSyncConfig.enableNetworkSandbox
      : this._networkSandboxOverride
  }

  get networkSandboxWhitelist(): string[] {
    return this.applicationDto?.failoverTestSyncConfig?.networkSandboxWhitelist || null
  }

  get networkSandboxAllowedDomains(): string[] {
    return this.applicationDto?.failoverTestSyncConfig?.networkSandboxAllowedDomains || []
  }

  get networkSandboxAllowedServices(): string[] {
    return this.applicationDto?.failoverTestSyncConfig?.networkSandboxAllowedServices || []
  }

  get advancedNetworkSandboxEnabled(): boolean {
    return this.applicationDto?.failoverTestSyncConfig?.advancedNetworkSandbox || this.type == ApplicationType.AZURE
  }

  get ransomwareRecoveryEnabled() {
    if (this._quarantineOverride === null) {
      return this.applicationDto.restoreConfig && this.applicationDto.restoreConfig.enableQuarantine
    } else {
      return this._quarantineOverride
    }
  }

  get recoveryPoints() {
    return this._recoveryPoints
  }

  get currentRecoveryPointId() {
    return this.applicationDto.recoveryPointId
  }

  get currentRecoveryPoint(): RecoveryPointDto {
    return (
      this.recoveryPoints &&
      this.recoveryPoints.reduce(
        (accum, item) => (item.recoveryPointId === this.currentRecoveryPointId ? item : accum),
        null
      )
    )
  }

  get recoveryPointAttempts() {
    return this._recoveryPointAttempts
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Processing Timestamps
  ////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////// BACKUP TIMESTAMPS /////////////////////////////////////

  get currentlyBackingUp() {
    return this.backupStatus && maxDate(this.lastBackupRequest, this.lastBackupStart) > this.lastBackupComplete
  }

  get lastBackupRequest() {
    return this.applicationDto.backupStatus?.lastRequest
  }

  get lastBackupStart() {
    return this.applicationDto.backupStatus?.lastStart
  }

  get lastBackupSuccess() {
    return this.applicationDto.backupStatus?.lastSuccess
  }

  get lastBackupFailure() {
    return this.applicationDto.backupStatus?.lastFailure
  }

  get lastBackupComplete() {
    return maxDate(this.backupStatus?.lastFailure, this.backupStatus?.lastSuccess)
  }

  get backupRecentlyCompleted() {
    return this.backupStatus && this.lastBackupComplete && dateDiff(new Date(), this.lastBackupComplete) < 30 * 1000
  }

  get deletedAt() {
    return this.applicationDto.deletedAt
  }

  get lastRestoreResourcesRemain(): boolean {
    return this.applicationDto.lastRestoreResourcesRemain
  }

  private processingHorizon(processingRequest: Date, processingStart: Date, processingComplete): Date {
    // This function is used to determine which resource status should be filtered out when
    // we get resource status from the server.
    if (isLaterThan(processingStart, processingComplete)) {
      // If processing is in progress, use start time.
      return processingStart
    } else if (isLaterThan(processingRequest, processingComplete)) {
      // If a request has happened since processing completed, use request time.
      return processingRequest
    }
    // Default to the time the app was created.
    return this.createdAt
  }

  get backupHorizon(): Date {
    return this.processingHorizon(this.lastBackupRequest, this.lastBackupStart, this.lastBackupComplete)
  }

  /** Latch the restore-request time locally, along with the DTO start it has to outlive. */
  private latchRestoreRequest() {
    this._localRestoreRequestAt = new Date()
    this._localRestoreRequestBaselineStart = this.lastRestoreStart ?? null
    this._localRestoreRequestDeadline = Date.now() + Application.LOCAL_RESTORE_LATCH_TTL
  }

  /** Adopt the request timestamp the backend stamped, which the restore response carries, so the
   *  latch stops being a client clock reading the moment the real one is available. Ignores a
   *  timestamp that doesn't postdate the restore being masked, since the latch is only useful
   *  while it sits ahead of that restore's statuses. */
  private confirmRestoreRequestLatch(requestedAt: Date) {
    if (!this._localRestoreRequestAt) return

    // Re-stamp the deadline from the response rather than the click: read lag only starts once
    // the write lands, and the window is now short enough that the round trip would eat into it.
    this._localRestoreRequestDeadline = Date.now() + Application.LOCAL_RESTORE_LATCH_TTL

    if (isLaterThan(requestedAt, this._localRestoreRequestBaselineStart)) {
      this._localRestoreRequestAt = requestedAt
    }
  }

  private clearRestoreRequestLatch() {
    this._localRestoreRequestAt = null
    this._localRestoreRequestBaselineStart = null
    this._localRestoreRequestDeadline = null
  }

  /** The latched restore-request time while it's still meaningful, else null. Released once the
   *  DTO reports a newer restore start than the one we latched against, since from then on the
   *  DTO can drive currentlyRestoring and restoreHorizon on its own, or once the deadline passes
   *  for a request the backend never registered at all. */
  private get _pendingLocalRestoreRequest(): Date | null {
    const latch = this._localRestoreRequestAt
    if (!latch) return null

    // Released on lastStart rather than lastRequest so a previous restore that never completed
    // can't have its start win processingHorizon while the new one is still queued on locks.
    // Past the deadline the DTO's own lastRequest carries the masking either way.
    if (
      isLaterThan(this.lastRestoreStart, this._localRestoreRequestBaselineStart) ||
      Date.now() >= this._localRestoreRequestDeadline
    ) {
      this.clearRestoreRequestLatch()
      return null
    }
    return latch
  }

  /** Restore request time, preferring the latch so a poll that predates the request can't
   *  make the restore look finished. */
  private get _effectiveLastRestoreRequest(): Date {
    return maxOrAnyDate(this.lastRestoreRequest, this._pendingLocalRestoreRequest)
  }

  get restoreHorizon(): Date {
    // A just-requested restore is newer than any prior start or completion, so it wins
    // outright — processingHorizon would otherwise return the previous restore's start
    // and leave that restore's statuses looking current.
    const pending = this._pendingLocalRestoreRequest
    if (pending) return pending
    return this.processingHorizon(this.lastRestoreRequest, this.lastRestoreStart, this.lastRestoreComplete)
  }

  get failbackHorizon(): Date {
    return this.processingHorizon(this.lastFailbackRequest, this.lastFailbackStart, this.lastFailbackComplete)
  }

  /////////////////////////////////// RESTORE TIMESTAMPS /////////////////////////////////////

  get currentlyRestoring() {
    if (this.restoreStatus) {
      const status = computeProcessStatus(
        this._effectiveLastRestoreRequest,
        this.lastRestoreStart,
        this.lastRestoreSuccess,
        this.lastRestoreFailure
      )
      return (
        status === ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS || status === ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS
      )
    }

    return false
  }

  get restoreRecentlyCompleted() {
    return this.restoreStatus && this.lastRestoreComplete && dateDiff(new Date(), this.lastRestoreComplete) < 30 * 1000
  }

  get firstRestoreCompletedInTest(): boolean {
    return this._firstRestoreCompletedInTest
  }

  get lastRestoreRequest() {
    return this.applicationDto.restoreStatus?.lastRequest
  }

  get lastRestoreStart() {
    return this.applicationDto.restoreStatus?.lastStart
  }

  get lastRestoreSuccess() {
    return this.applicationDto.restoreStatus?.lastSuccess
  }

  get lastRestoreFailure() {
    return this.applicationDto.restoreStatus?.lastFailure
  }

  get lastRestoreComplete(): Date {
    return maxDate(this.lastRestoreFailure, this.lastRestoreSuccess)
  }

  /////////////////////////////////// FAILBACK TIMESTAMPS /////////////////////////////////////

  get currentlyFailingBack() {
    return this.failbackStatus && maxDate(this.lastFailbackRequest, this.lastFailbackStart) > this.lastFailbackComplete
  }

  get lastFailbackRequest() {
    return this.applicationDto.failbackStatus?.lastRequest
  }

  // Fixed: was incorrectly returning lastRequest — lastStart is the actual job start time
  get lastFailbackStart() {
    return this.applicationDto.failbackStatus?.lastStart
  }

  get lastFailbackSuccess() {
    return this.applicationDto.failbackStatus?.lastSuccess
  }

  get lastFailbackFailure() {
    return this.applicationDto.failbackStatus?.lastFailure
  }

  get lastFailbackComplete() {
    return maxDate(this.lastFailbackSuccess, this.lastFailbackFailure)
  }

  get lastFailback() {
    return this.applicationDto.failbackStatus
  }

  get failbackRecentlyCompleted() {
    return (
      this.failbackStatus && this.lastFailbackComplete && dateDiff(new Date(), this.lastFailbackComplete) < 30 * 1000
    )
  }

  get currentlyActive() {
    return this.currentlyBackingUp || this.currentlyRestoring || this.currentlyFailingBack
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Issue Accessors
  ////////////////////////////////////////////////////////////////////////////////

  /** Adapt the in-scope issue DTOs and collapse duplicate reports of the same problem.
   *  Deduping here rather than in the view is what keeps every consumer agreeing: the
   *  sidebar badge, the "All Issues (N)" tab, the "Review and fix N issues" status links,
   *  the CSV export and each resource's issue set all count what the Issues page renders. */
  private buildIssueSection(scopes: ApplicationIssueScopeDto[]): IssueSectionRow[] {
    if (!this._applicationIssueDtos) return []
    const rows = this._applicationIssueDtos
      .filter((issueDto) => scopes.includes(issueDto.scope))
      .map((issueDto) => {
        const raised = adaptApplicationIssueDtoToRaisedIssue(issueDto, this)
        // No resourceName here: resource DTOs aren't loaded for every app, and the ARN in the
        // dedup key already pins the resource. See DedupableIssue.
        return { dto: issueDto, raised, issue: raised.issue, arn: raised.arn, resourceName: null }
      })
    return dedupeIssues(rows)
  }

  /** Three deduped sections, mirroring how the Issues page splits issues into Backup /
   *  Recovery / Failback. Dedup runs per section, so a problem reported during both backup
   *  and restore survives in each. Rebuilt once per poll rather than per access — several
   *  template bindings read these getters on every change detection pass. */
  private get issueSections(): IssueSections {
    // Don't memoize before the first fetch — the sidebar and the app page both read these
    // getters while the DTOs are still null, and caching that empty result would depend on
    // setIssues being the only writer to ever undo it.
    if (!this._applicationIssueDtos) return NO_ISSUE_SECTIONS
    if (!this._issueSections) {
      const recoveryPointRows = this.buildIssueSection([
        ApplicationIssueScopeDto.SCAN,
        ApplicationIssueScopeDto.REFRESH,
        ApplicationIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT,
        ApplicationIssueScopeDto.BACKUP,
      ])
      const restore = this.buildIssueSection([ApplicationIssueScopeDto.RESTORE]).map((row) => row.raised)
      const failback = this.buildIssueSection([ApplicationIssueScopeDto.FAILBACK]).map((row) => row.raised)
      const recoveryPoint = recoveryPointRows.map((row) => row.raised)
      this._issueSections = {
        recoveryPoint,
        // scan and backup are a display partition of the already-deduped recovery-point
        // section, not sections of their own: a problem reported at both scan time and
        // backup time collapses to one row that lands in whichever bucket its scope says.
        // Consumers must union the two rather than read either alone. The split reads the
        // DTO scope because REFRESH has no RaisedIssueScope equivalent.
        scan: recoveryPointRows.filter((row) => row.dto.scope !== ApplicationIssueScopeDto.BACKUP).map((r) => r.raised),
        backup: recoveryPointRows
          .filter((row) => row.dto.scope === ApplicationIssueScopeDto.BACKUP)
          .map((r) => r.raised),
        restore,
        failback,
        // Every scope except CLEANUP, which we never show
        all: [...recoveryPoint, ...restore, ...failback],
      }
    }
    return this._issueSections
  }

  get scanIssues() {
    return this.issueSections.scan
  }

  get backupIssues() {
    return this.issueSections.backup
  }

  get restoreIssues() {
    return this.issueSections.restore
  }

  get failbackIssues() {
    return this.issueSections.failback
  }

  // Distinguishes "not yet fetched" (null) from "fetched but empty" ([]) for loading state display
  get issuesLoaded(): boolean {
    return this._applicationIssueDtos !== null
  }

  get allIssues() {
    return this.issueSections.all
  }

  get recoveryPointIssues() {
    return this.issueSections.recoveryPoint
  }

  deleteApplicationIssue$(issueId: string, scope: ApplicationIssueScopeDto) {
    return this.apiService.deleteApplicationIssue(this.accountId, this.applicationId, scope.toString(), issueId)
  }

  setSyncPairResourceConfig$(arn: Arn, config: ResourceConfigDto) {
    return this._syncPair.setSyncPairResourceConfig$(arn, config)
  }

  getSyncPairResourceConfig$(arn: Arn) {
    return this._syncPair.getSyncPairResourceConfig$(arn)
  }
  get manualResourceTemplateUrl$() {
    return this._syncPair.manualResourceTemplateUrl$
  }

  /** Important to feed syncPair data down the component tree */
  get syncPair(): SyncPair {
    return this._syncPair
  }
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationProviderService {
  // One list request per cycle covers every application, so this cadence is independent of how
  // many applications the account has.
  static LIST_POLL_INTERVAL = 60000

  // cache the latest version of each application
  private applicationCache: Map<string, Application> = null
  private accountId: string = null
  private _loadingAllApplications = false
  private _forceListPoll$ = new Subject<void>()
  // Emits true when loadAllApplications$ completes (success or error), false when a new load starts.
  // Used by AccountRootComponent to wait for apps before making a routing decision.
  private _applicationsReady$ = new BehaviorSubject<boolean>(false)

  constructor(
    private apiService: ApiService,
    private ssProviderService: SyncSourceProviderService,
    private spProviderService: SyncPairProviderService,
    private accountService: AccountService,
    private _pageVisibility: PageVisibilityService, // injection forces singleton creation before Application objects exist
    private crossTabSync: CrossTabSyncService
  ) {
    // subscribe to publications so that we can maintain the cache
    Application.publishSubject$.subscribe((crudEvent) => {
      if (
        crudEvent.action == CrudAction.UPSERT &&
        crudEvent.entity.accountId == this.accountId &&
        !!this.applicationCache
      ) {
        this.applicationCache.set(crudEvent.entityId, crudEvent.entity)
      } else if (!!this.applicationCache) {
        this.applicationCache.delete(crudEvent.entityId)
      }
    })

    // When another tab/window reports a change, re-poll that app immediately.
    this.crossTabSync.applicationChanged$.subscribe(({ accountId, applicationId, optimisticPhase }) => {
      if (accountId !== this.accountId) return
      const app = this.getCachedApplication(applicationId)
      if (!app) return
      // replace stale override
      if (optimisticPhase) app.applyOptimisticSyncPhase(optimisticPhase)
      app.forceImmediatePoll()
      app.refresh$(true, false).subscribe()
    })
  }

  loadedApplications() {
    if (!this.applicationCache) {
      return []
    }
    return Array.from(this.applicationCache.values())
  }

  /**
   * Returns the set of identity provider IDs that are used by applications.
   * These are typically MS Entra IdPs used for Azure environment access,
   * and should be excluded from SSO user management IdP dropdowns.
   */
  getUsedIdentityProviderIds(): Set<string> {
    const idpIds = new Set<string>()
    for (const app of this.loadedApplications()) {
      if (app.identityProviderId) {
        idpIds.add(app.identityProviderId)
      }
    }
    return idpIds
  }

  /**
   * Invalidate the ready state and clear the cache before a new account load begins.
   * Must be called BEFORE loadAccount$() so that AccountRootComponent sees ready=false
   * when accountIdSource.next() fires inside loadAccount$'s map — otherwise it can pick up
   * a stale ready=true from the previous account's load and route to the welcome page.
   */
  prepareForAccountLoad(accountId: string): void {
    this.applicationCache = new Map<string, Application>()
    this.accountId = accountId
    this._applicationsReady$.next(false)
  }

  subscribeToApplications$(accountId: string) {
    if (!!this.accountId && accountId != this.accountId) {
      throw Error('Subscribing to applications on the wrong account ID.')
    }

    let publishSubject$ = Application.publishSubject$ as Observable<CrudEvent<Application, string>>
    if (!!this.applicationCache) {
      // If we have cached applications, publish them immediately
      publishSubject$ = concat(
        from(
          Array.from(this.applicationCache.values()).map((application) => {
            return {
              action: CrudAction.UPSERT,
              entity: application,
              entityId: application.applicationId,
            } as CrudEvent<Application>
          })
        ),
        publishSubject$
      )
    }

    return publishSubject$
  }
  loadAllApplications$(accountId: string) {
    /**
     * Query the server for all applications. Bypasses the cache — used to populate it.
     *
     * Why this block "duplicates" prepareForAccountLoad():
     *
     * The two clears cover different timelines. prepareForAccountLoad() runs SYNCHRONOUSLY
     * before loadAccount$() is even called, so by the time loadAccount$ internally fires
     * `accountIdSource.next(accountId)` downstream subscribers (notably AccountRootComponent,
     * which gates its welcome-page redirect on _applicationsReady$) already observe ready=false.
     * Without that pre-flight, the subject emission races the cache reset and AccountRoot
     * can read a stale ready=true from the previous account and redirect to welcome.
     *
     * This in-method reset can't close that race — it runs inside the observable's
     * subscription, which is *after* accountIdSource.next has already notified subscribers.
     * It exists for the other code path: callers that invoke loadAllApplications$ directly
     * (e.g. standalone refresh flows) without going through prepareForAccountLoad → loadAccount$.
     * Those callers don't hit the subject-emission race, so resetting here is sufficient
     * and prevents stale cache / ready state from leaking into the new query.
     */
    if (this.accountId !== accountId || this._applicationsReady$.value !== false) {
      this.applicationCache = new Map<string, Application>()
      this.accountId = accountId
      this._applicationsReady$.next(false)
    }

    // We need to track the internal state of whether this observable is currently active.
    // To do that, we set a flag upon subscription, but building the actual observable inside
    // of a switchMap which will execute immediately upon subscribing to this observable.
    return of(null).pipe(
      switchMap((_null) => {
        this._loadingAllApplications = true
        return Application.loadAllApplications$(
          accountId,
          this.apiService,
          this.accountService,
          this.ssProviderService,
          this.spProviderService
        )
      }),
      finalize(() => {
        this._loadingAllApplications = false
        // Signal that apps are loaded (or load failed) — safe to check cache now
        this._applicationsReady$.next(true)
      })
    )
  }

  /** Keep every application in the cache current, using one list request per cycle.
   *
   *  The list and per-application endpoints serialize the same DTO, so a single listApplications
   *  call refreshes the whole account without a request per application. */
  startBackgroundRefreshForAllApps$() {
    const pollIntervals = new BehaviorSubject<number>(0)
    return pollIntervals.pipe(
      // throttled when tab is hidden
      switchMap((pollInterval) =>
        race(visibilityAwareTimer(pollInterval), this._forceListPoll$.pipe(map(() => 0 as 0)))
      ),
      switchMap(() =>
        this.apiService.listApplications(this.accountId).pipe(
          switchMap((appDtos) => this._applyApplicationList$(appDtos)),
          catchError((err) => {
            console.warn('Background application list refresh error, scheduling retry:', err)
            return of(null)
          })
        )
      ),
      tap(() => pollIntervals.next(ApplicationProviderService.LIST_POLL_INTERVAL))
    )
  }

  /** Trigger an immediate list poll, bypassing the current interval. */
  forceImmediateListPoll(): void {
    this._forceListPoll$.next()
  }

  // Updates cached applications in place and creates any that appeared. Applications missing from
  // the response are left alone: the list endpoint filters by permission and deletion state, so
  // absence isn't proof of removal.
  private _applyApplicationList$(appDtos: Array<ApplicationDto>): Observable<Array<Application>> {
    const updates = appDtos.map((appDto) => {
      const cached = this.applicationCache?.get(appDto.appId)
      if (cached) {
        return cached.applyBackgroundDto$(appDto)
      }
      // Application.create publishes the new object, which populates the cache via the
      // publishSubject$ subscription in the constructor.
      return of(
        Application.create(appDto, this.apiService, this.accountService, this.ssProviderService, this.spProviderService)
      )
    })

    if (updates.length === 0) {
      return of([])
    }
    return forkJoin(updates)
  }

  get loadingAllApplications() {
    return this._loadingAllApplications
  }

  /** Observable that emits true once loadAllApplications$ has finished (success or error). */
  get applicationsReady$(): Observable<boolean> {
    return this._applicationsReady$.asObservable()
  }

  getCachedApplication(applicationId: string): Application | null {
    return this.applicationCache?.get(applicationId) ?? null
  }

  loadApplication$(
    applicationId: string,
    accountId: string,
    refreshDto: boolean = true,
    forceRefreshResources: boolean = false
  ) {
    if (!!this.applicationCache && this.applicationCache.has(applicationId)) {
      let application = this.applicationCache.get(applicationId)
      if (application.accountId != accountId) {
        throw 'The application cache contains applications from the wrong account ID.'
      }
      // When forceRefreshResources is true (editor save redirect), clear stale resource
      // caches UNLESS optimistic resources were already injected by the editor.
      // hasOptimisticResources checks if setOptimisticBackupResources() was called.
      if (forceRefreshResources && !application.hasOptimisticResources) {
        application.invalidateResourceCaches()
      }
      if (application.isLoaded) {
        return concat(of(application), application.refresh$(refreshDto, true, forceRefreshResources))
      }
      return application.refresh$(refreshDto, true, forceRefreshResources)
    }
    return Application.loadApplication$(
      accountId,
      applicationId,
      this.apiService,
      this.accountService,
      this.ssProviderService,
      this.spProviderService,
      forceRefreshResources
    )
  }

  createApplication$(
    accountId: string,
    applicationType: ApplicationType,
    applicationName: string,
    applicationRpo: number,
    applicationNotificationEmails: Array<string>,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    selectionTags: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    selectionArns: Set<Arn>,
    excludeWeakArns: Set<Arn>,
    identityProviderId: string = null
  ) {
    // Mark exclusion tags with action='exclude'
    const markedExclusionTags = excludingRules.map((tag) => {
      if (tag instanceof ResourceTypeRule || tag instanceof ResourceGroupRule || tag instanceof AllRule) {
        // These objects should already handle the action in their internal structure
        return tag
      } else {
        // For regular tags, add the action property
        return { ...tag, action: 'exclude' } as Tag
      }
    })

    const combinedTags = [...selectionTags, ...markedExclusionTags]

    return Application.createApplication$(
      this.apiService,
      this.accountService,
      this.ssProviderService,
      this.spProviderService,
      accountId,
      applicationType,
      applicationName,
      applicationRpo,
      applicationNotificationEmails,
      sourceEndpointUri,
      targetEndpointUri,
      combinedTags,
      selectionArns,
      excludeWeakArns,
      identityProviderId
    )
  }
}
